/**
 * The project shell: CHROME around a tree of CONTENT panes.
 *
 * Two different things, deliberately not one mechanism (the lesson of the version
 * this replaces, which put everything in one tree):
 *
 *   chrome   the files sidebar and the settings pane. One each,
 *            never split, toggled. Flags and a width in useLayout.
 *   content  agent blocks and document blocks. Repeatable, nestable, dragged
 *            around, addressed by leaf id, and carried in the url.
 *
 * An AGENT BLOCK is one thing whose state is which session it is on — or none,
 * meaning the session list. That is what keeps the back button: picking a session
 * and coming back is one block changing what it shows, not panes opening and
 * closing. Two conversations at once is two blocks.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  type AppSummary,
  type AttachmentRef,
  attachFile,
  createSession,
  handledKeys,
  installedApps,
  installLayout,
  installRelease,
  isSessionDone,
  layoutsIn,
  layoutTree,
  type ProjectDoc,
  projectDocuments,
  projectDocumentsOfType,
  projectString,
  resolveHandler,
  resolveLayout,
  type SettingValue,
  type Slot,
  sessionTitleOf,
  setAppSetting,
  setHandler,
  setLayoutGeometry,
  setLayoutSlots,
  setLayoutTree,
  setSessionCompleted,
  slotFill,
  slotGeometry,
  sourceOrigin,
  unregisterApp,
  updateFromRelease,
  worldAgentSessionId,
} from '@mod/core/model'
import { cancelRun } from '@mod/core/run'
import { Columns2, X } from 'lucide-react'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ARRANGE_PRESETS,
  arrangementName,
  type ContentLeaf,
  leaf,
  leafAt,
  leafShowing,
  leaves,
  paneTabs,
} from '../../features/code/contentTree'
import type { LinkOpenTarget } from '../../features/documents/DocumentLink'
import { openOrCreateInstance } from '../../features/documents/NewInstanceGate'
import { DiscoverPage } from '../../features/packages/DiscoverPage'
import {
  type AppRole,
  PackagesModal,
} from '../../features/packages/PackagesModal'
import { usePackageUpdates } from '../../features/packages/packageUpdates'
import {
  publishInstalled,
  uploadScreenshot,
} from '../../features/packages/publishInstalled'
import { TerminalPanel } from '../../features/terminal/TerminalPanel'
import { useLocalTerminals } from '../../features/terminal/useLocalTerminals'
import {
  browserProcessImage,
  fileToSource,
} from '../../features/thread/attachments'
import { preferredComposerModel } from '../../features/thread/Composer'
import { ComposerSlotProvider } from '../../features/thread/ComposerSlot'
import { SessionHoldingsBar } from '../../features/thread/HoldingsBar'
import { ComposerRefs } from '../../features/thread/RefPicker'
import { SelectionProvider } from '../../features/thread/SelectionProvider'
import { SessionActivity } from '../../features/thread/SessionActivity'
import { SessionTasksChip } from '../../features/thread/SessionTasksChip'
import { sessionCalls } from '../../features/thread/sessionCall'
import {
  sendToSession,
  startSessionWithMessage,
  TaskTaken,
} from '../../features/thread/startSession'
import { answerPendingWithText } from '../../features/thread/stripQuestion'
import { useSessionList } from '../../features/thread/useSessionList'
import { useSessionProject } from '../../features/thread/useSessionProject'
import { useSessionVoice } from '../../features/thread/useSessionVoice'
import { notePage as noteWebPage } from '../../features/web/driving'
import { canEmbedWebPages, webFileFor } from '../../features/web/webPage'
import { WindowProvider } from '../../sdk/WindowProvider'
import { AppIconsProvider } from '../../services/apps/AppIcon'
import type { AppViewController } from '../../services/apps/appControl'
import { projectChoices } from '../../services/sessions/projectChoices'
import {
  STATUS_ROW_LIMIT,
  useSessionStatuses,
} from '../../services/sessions/sessionStatus'
import { SpaceSessionProvider } from '../../services/sessions/spaceSession'
import { useOpenTouches } from '../../services/sessions/useOpenTouches'
import { appSkillOf } from '../../services/skills/skillCatalog'
import { useSkills } from '../../services/skills/useSkills'
import { unownedTasks } from '../../services/tasks/registry'
import { capturePaneImages } from '../../services/view/capturePaneImages'
import {
  type MessageViewContext,
  viewManifest,
} from '../../services/view/viewManifest'
import {
  startVoiceOnOpen,
  voiceBeforeSession,
} from '../../services/voice/startOnOpen'
import { missingCoreApi } from '../../services/world/coreApi'
import { useDocument } from '../../services/world/repo/useDocument'
import { useComposerDevices } from '../../services/world/useComposerDevices'
import { useInstalledApps } from '../../services/world/useInstalledApps'
import { useProjectHosting } from '../../services/world/useProjectHosting'
import { useRegistryStatuses } from '../../services/world/useRegistryStatuses'
import { useWorldHandlers } from '../../services/world/useWorldHandlers'
import { useWorldProjects } from '../../services/world/useWorldProjects'
import {
  useWorldActivity,
  useWorldSessions,
} from '../../services/world/useWorldSessions'
import { readWorldPointer } from '../../services/world/world'
import { registerBuiltins } from '../slots/builtins'
import { sessionsInReach } from '../slots/composer/composerSlot'
import { InstalledSlot } from '../slots/InstalledSlot'
import {
  AGENT_RENDERER_KEY,
  handlerFor,
  resolveRenderer,
} from '../slots/renderers'
import { fillSlot } from '../slots/slots'
import { ThreadSlot } from '../slots/ThreadSlot'
import { useEdgeReveal } from '../slots/useEdgeReveal'
import {
  homeSpaceOf,
  spaceActivity,
  spaceLabel,
  spaceSessions,
  statusReadOrder,
} from '../spaces/spaceRows'
import { useProjectDocs } from '../spaces/useProjectCatalogs'
import { useProjectLayer } from '../spaces/useProjectLayer'
import { useSpaceLayer } from '../spaces/useSpaceLayer'
import {
  findSpaceByName,
  noteSessionSpaces,
  planOpenForSession,
  planWebOpenForSession,
  publishAgentView,
  sessionHomeSpace,
  treeHoldsSession,
} from './agentViewApi'
import { CallPill } from './CallPill'
import { CanvasPanel } from './CanvasPanel'
import { ContentPanes } from './ContentPanes'
import { composerShows } from './composerShows'
import { decodeContent, encodeContent } from './contentUrl'
import { type CommandTarget, targetsElsewhere } from './launcherTargets'
import { type NewPaneItem, NewPanePanel } from './NewPanePanel'
import { OnboardingFlow } from './onboarding/OnboardingFlow'
import {
  isOnboardingPending,
  restartOnboarding,
} from './onboarding/onboardingState'
import { Panel, PanelAction } from './Panel'
import { ResizeHandle } from './ResizeHandle'
import { SettingsModal, SettingsPanel } from './SettingsPanel'
import {
  appAsFile,
  currentViewPaths,
  isAppRow,
  isBrowsableDocument,
} from './sidebarItems'
import { fileBreadcrumbs, type Subject } from './subject'
import { useLayout } from './useLayout'
import { load as loadOpenIds, noteOpenedIn, useOpenItems } from './useOpenItems'
import { useProjectKeymap } from './useProjectKeymap'
import { WorldSummary, worldSummary } from './WorldSummary'
import { WorldView } from './WorldView'
import { renameWorldAgent, worldAgentName } from './worldAgentName'

/**
 * RFC 0284 S3/S6. The shipped sidebar, header and thread go into the registries
 * HERE, so the shell then asks what fills each slot and what draws a session and
 * resolves its own chrome exactly as it will resolve an installed fill.
 *
 * A call rather than three side-effect imports, which is what this was. An
 * `import '../slots/sidebar/sidebarSlot'` with no binding looks like something to tidy
 * away, and deleting it takes the sidebar with it at runtime without failing a
 * build. See `chrome/builtins.ts`.
 */
registerBuiltins()

/**
 * ⌘K's id for "make me a shell".
 *
 * `create:` so it sorts with the other create actions and gets their ranking
 * boost — typing "term" should offer to MAKE one above the ones you have, the
 * same way "review" does. The suffix is not a data type, which is why
 * `onPickTarget` matches this id before it parses one out.
 */
const TERMINAL_CREATE_ID = 'create:terminal'

const EDGE_PX = 8
const REVEAL_DELAY = 120
const HIDE_DELAY = 160
/** What the header opens at when a layout does not say (RFC 0284 S6d). Matches
 *  the `h-9` the shell used to hard-code, so this changes nothing on its own. */
const HEADER_HEIGHT_PX = 36
/** What an INSTALLED composer is given when a layout does not say (S8d). Tall
 *  enough for the box, its control row and one line of text: shorter and the
 *  thing you type into is the thing that gets cropped. The shipped one is not
 *  given a height at all, because it overlays the panes and sizes itself. */
const COMPOSER_HEIGHT_PX = 96

/** How a pane reads to the agent: what it is, and what it is on. */
/** A hidden space's panes cannot be dragged or resized: nothing to see. */

const describePane = (pane: ContentLeaf): string => {
  if (pane.content.kind === 'picker') return 'empty pane'
  if (pane.content.kind === 'discover')
    return pane.content.lineageId
      ? `package ${pane.content.lineageId}`
      : 'Discover'
  if (pane.content.kind === 'agent')
    return pane.content.sessionId
      ? `session ${pane.content.sessionId}`
      : 'session list'
  if (pane.content.kind === 'terminal')
    return pane.content.terminalId
      ? `terminal ${pane.content.terminalId}`
      : 'terminal list'
  return `document ${pane.content.docId}`
}

export function WorldShell(props: { projectId: string }) {
  /**
   * REFUSE TO DRAW AGAINST A STALE MOD-CORE, and say which build is behind.
   *
   * The app runs from a dev server and mod-core from a built package, so a
   * shell file hot-reloads the moment it is edited while mod-core does not.
   * Calling an export that is not in the build yet throws deep inside a render,
   * React unmounts everything, and the window goes blank with a stack that
   * names the wrong thing — nothing in it says "rebuild mod-core", which is the
   * only sentence that helps. Checked ABOVE every hook so a shell that cannot
   * work says so instead of half-running.
   */
  const missing = missingCoreApi()
  if (missing.length > 0) return <StaleCoreNotice missing={missing} />
  return <Shell projectId={props.projectId} />
}

/** What the shell cannot do without, said as an instruction. */
function StaleCoreNotice({ missing }: { missing: string[] }) {
  return (
    <div className="ws-next flex h-screen flex-col items-center justify-center gap-3 bg-background p-8 text-center text-foreground">
      <h1 className="font-semibold text-sm">@mod/core is out of date</h1>
      <p className="max-w-[60ch] text-muted-foreground text-xs leading-normal">
        The app is running from source and mod-core from a build that predates
        it. Quit the app and rebuild, then start it again.
      </p>
      <pre className="rounded border border-border bg-muted px-3 py-2 text-left text-[11px]">
        corepack pnpm --filter @mod/core build{'\n'}
        corepack pnpm --filter @mod/mod-desktop build
      </pre>
      <p className="max-w-[60ch] text-[11px] text-muted-foreground/70 leading-normal">
        Missing: {missing.join(', ')}
      </p>
    </div>
  )
}

function Shell({ projectId }: { projectId: string }) {
  const repo = useRepo() as unknown as Repo
  /**
   * The space this window is in (RFC 0281 S4). Mounted here so it runs for the
   * lifetime of the shell: it seeds the url from the space on arrival and
   * writes the tree back as it changes, and both stop the moment it unmounts.
   */
  const spaceLayer = useSpaceLayer(repo)
  // Handed every space, so the ones not on screen can be rendered hidden and
  // keep their panes' scroll, ptys and iframes across a switch.
  const layout = useLayout({
    spaces: spaceLayer.spaces,
    spaceId: spaceLayer.spaceId,
  })
  const { chrome, tree, focusedId } = layout
  /** Mounted app command doors, keyed by pane so only the focused one answers. */
  const appControls = useRef(new Map<string, AppViewController>())
  const appControlsByDoc = useRef(new Map<string, AppViewController>())
  const [projectDoc] = useDocument(projectId as AnyDocumentId)

  /**
   * Everything this window can see (RFC 0295 S2).
   *
   * The World's catalog merged with this project's, rather than the project's
   * alone. Chrome is installed in the World, because a window binds to one and
   * RFC 0290 lets it hold several projects, so a sidebar or a thread renderer
   * read off `projectApps` would be invisible in every project but the one it
   * was installed into.
   */
  const installedAppList = useInstalledApps(projectDoc)
  /** This window's project's skills, for an app's "Open skill" (RFC 0334 3g). */
  const windowSkills = useSkills(projectDoc)
  /** Which package a person chose to open each type (RFC 0284 S6m). */
  const worldHandlers = useWorldHandlers()
  /** Which app opens a file of this mime, for its tile (RFC 0332): the same
   *  answer opening the file gets, person's choice included. */
  const editorFor = useCallback(
    (mime: string) => handlerFor(installedAppList, mime, worldHandlers)?.id,
    [installedAppList, worldHandlers],
  )

  /**
   * The layout this window is arranged by (RFC 0284 §4e, S4b).
   *
   * The space names it and the catalog answers, so switching space can switch
   * arrangement and neither the shell nor the sidebar decides which one wins.
   *
   * `undefined` for a space that names none, and equally for one naming a
   * layout nobody has installed. Both mean the built-in default, which is what
   * keeps a window drawing after somebody uninstalls the layout it was using.
   */
  const spaceLayout = useMemo(
    () =>
      resolveLayout(
        installedAppList,
        spaceLayer.spaces.find((sp) => sp.spaceId === spaceLayer.spaceId)
          ?.layoutId,
      ),
    [installedAppList, spaceLayer.spaces, spaceLayer.spaceId],
  )

  /** What Settings offers to arrange this space with (RFC 0284 S4d). */
  const layoutChoices = useMemo(
    () =>
      layoutsIn(installedAppList).map((l) => ({
        id: l.id,
        name: l.name,
        description: l.description,
      })),
    [installedAppList],
  )

  const currentLayoutId =
    spaceLayer.spaces.find((sp) => sp.spaceId === spaceLayer.spaceId)
      ?.layoutId ?? null

  /**
   * Installed fills that can fill a given slot (RFC 0284 S5, S6c).
   *
   * By SLOT rather than one list per surface, so the header cost nothing here
   * and the panel does not learn a new field per slot.
   */
  const choicesFor = useCallback(
    (slot: Slot) =>
      installedAppList.flatMap((a) =>
        a.realm === 'host'
          ? (a.panels ?? [])
              .filter((c) => c.slot === slot)
              .map((c) => ({ id: c.id, name: c.title || a.name }))
          : [],
      ),
    [installedAppList],
  )

  const sidebarFill = slotFill(spaceLayout, 'sidebar')
  const headerFill = slotFill(spaceLayout, 'header')
  const composerFill = slotFill(spaceLayout, 'composer')

  /**
   * Change what this layout puts in a slot (RFC 0284 S5, S6c).
   *
   * Only reachable when a layout is selected, because editing the BUILT-IN
   * default would mean writing a layout nobody chose and then silently
   * switching to it.
   */
  const setSlot = useCallback(
    (slot: Slot, fill: string[] | undefined) => {
      const worldId = readWorldPointer()
      if (!worldId || !currentLayoutId) return
      // ONE WRITE, built by hand rather than by spreading and then deleting.
      // `undefined` has to leave the key ABSENT, because absent means "use the
      // built-in" and `[]` means "empty this slot", and the two must not blur.
      const next = { ...(spaceLayout?.slots ?? {}) }
      if (fill === undefined) delete next[slot]
      else next[slot] = fill
      void setLayoutSlots(repo as never, worldId, currentLayoutId, next)
    },
    [repo, currentLayoutId, spaceLayout],
  )

  /**
   * Change how this layout SHAPES a slot (RFC 0284 S6d).
   *
   * Reads the whole geometry map and writes it back, because
   * `setLayoutGeometry` replaces wholesale: a merge in the mutation would make
   * "back to the default" impossible to express, and that is the state this
   * most needs to reach.
   */
  const setGeometry = useCallback(
    (slot: Slot, next: { size?: number; reveal?: 'pinned' | 'hover' }) => {
      const worldId = readWorldPointer()
      if (!worldId || !currentLayoutId) return
      const all = { ...(spaceLayout?.geometry ?? {}) }
      // ABSENT, not an empty object. A slot that says nothing about its shape
      // must read as the shell's default, and `{}` would too, but writing one
      // per slot leaves a layout claiming decisions nobody made.
      if (next.size === undefined && (next.reveal ?? 'pinned') === 'pinned')
        delete all[slot]
      else all[slot] = next
      void setLayoutGeometry(repo as never, worldId, currentLayoutId, all)
    },
    [repo, currentLayoutId, spaceLayout],
  )

  /**
   * Uninstall a fill, and take it out of every layout (RFC 0284 S5d).
   *
   * BOTH, in that order. A layout naming a fill that no longer
   * resolves renders an EMPTY slot, because an unresolvable id is dropped
   * rather than substituted, so removing only the catalog entry would leave a
   * person with no sidebar and no explanation.
   *
   * An emptied fill becomes ABSENT rather than `[]`: they stopped naming
   * something, which means the built-in draws, not that they asked for no
   * sidebar.
   */
  const removeInstalled = useCallback(
    (appId: string) => {
      const worldId = readWorldPointer()
      // BY APP ID, or by CONTRIBUTION id. A chrome fill is named inside
      // its app's `panels`, and a renderer or a layout is named by the app
      // itself, so callers pass whichever they hold and this end takes either.
      const owner = installedAppList.find(
        (a) => a.id === appId || (a.panels ?? []).some((c) => c.id === appId),
      )
      if (!owner) return
      /**
       * THE CATALOG IT ACTUALLY LIVES IN (RFC 0295, S6f).
       *
       * `unregisterApp` takes the catalog to remove from, and the merged list
       * gave no way to know which. The Apps modal passed the project for every
       * row, so removing a World-installed sidebar silently did nothing: it
       * edited a map that never held the entry. `scope` is set by
       * `installedApps`, which is the only thing that knows.
       */
      const catalogId = owner.scope === 'world' ? worldId : projectId
      if (!catalogId) return
      void (async () => {
        /**
         * OUT OF EVERY LAYOUT FIRST, when there is a World to hold them.
         *
         * A layout naming a fill that no longer resolves renders an
         * EMPTY slot, because an unresolvable id is dropped rather than
         * substituted, so removing the entry alone would leave a person with no
         * sidebar and no explanation.
         */
        if (worldId) {
          for (const layout of layoutsIn(installedAppList)) {
            const slots = { ...(layout.layout?.slots ?? {}) }
            let changed = false
            for (const key of Object.keys(slots) as Slot[]) {
              const fill = slots[key]
              // The CONTRIBUTION ids this app owns, plus its own id, since a
              // caller may hold either.
              const names = new Set([
                appId,
                owner.id,
                ...(owner.panels ?? []).map((c) => c.id),
              ])
              if (!fill?.some((id) => names.has(id))) continue
              const next = fill.filter((id) => !names.has(id))
              // ABSENT, not `[]`. Removing the last id means they stopped
              // naming something, which is "use the built-in", not "I want no
              // sidebar".
              if (next.length === 0) delete slots[key]
              else slots[key] = next
              changed = true
            }
            if (changed)
              await setLayoutSlots(repo as never, worldId, layout.id, slots)
          }
        }
        await unregisterApp(repo as never, catalogId, owner.id)
      })()
    },
    [repo, installedAppList, projectId],
  )

  /**
   * What an installed entry DOES, for the Apps modal (RFC 0284 S6f).
   *
   * Settings used to have a section per kind, which read as three categories of
   * setting when they are one catalog with four kinds of entry in it. The kind
   * is a LABEL on a row now, and it lives beside the other installed things
   * rather than beside the preferences.
   */
  const roleOf = useCallback((app: AppSummary): AppRole => {
    if (app.presentation === 'layout') return 'Layout'
    if (app.realm === 'host') {
      if (app.handles?.includes('agent')) return 'Thread'
      const slot = (app.panels ?? []).find((c) => c.slot)?.slot
      if (slot === 'header') return 'Header'
      if (slot === 'sidebar') return 'Sidebar'
      if (slot === 'composer') return 'Composer'
    }
    // EVERY ROW GETS A KIND, including the ordinary one. Returning undefined
    // here left a poll app as the only unlabelled row, which reads as
    // "unknown" rather than as "app", and made the four chrome kinds look like
    // the special cases when they are simply the other four.
    return 'App'
  }, [])

  /** Whether it is in use, and what using it means. */
  const statusOf = useCallback(
    (app: AppSummary): string | undefined => {
      const role = roleOf(app)
      // An ordinary app's own description is the better sentence; there is no
      // "in use" question to answer for one.
      if (role === 'App') return undefined
      if (role === 'Layout')
        return currentLayoutId === app.id
          ? 'Arranging this space.'
          : app.description || 'An arrangement you can switch to.'
      if (role === 'Thread')
        // WHETHER IT IS THE ONE IN USE, since S6m made that a choice. It used
        // to say "Remove to get the shipped thread back", which was true and
        // is no longer: Settings (⌘,) under Open with switches without
        // uninstalling anything.
        return resolveHandler(installedAppList, 'agent', worldHandlers).app
          ?.id === app.id
          ? 'Drawing every session. Change it under Open with in Settings.'
          : 'Installed. Choose it under Open with in Settings.'
      const slot: Slot = role === 'Header' ? 'header' : 'sidebar'
      const inUse = (app.panels ?? []).some(
        (c) => c.slot === slot && slotFill(spaceLayout, slot)?.includes(c.id),
      )
      return inUse
        ? 'Filling that slot in this layout.'
        : 'Installed. Name it in a layout to use it.'
    },
    [roleOf, currentLayoutId, spaceLayout, installedAppList, worldHandlers],
  )

  /**
   * A new space, opened on the current layout's panes (RFC 0284 S6h).
   *
   * Wrapped rather than pushed into `useSpaceLayer`, which knows about spaces
   * and deliberately not about the catalog. It takes a seed; the shell is what
   * knows where a seed comes from.
   */
  const newSpace = useCallback(
    (name?: string) =>
      spaceLayer.createSpace(name, {
        content: layoutTree(spaceLayout),
        // The layout comes with the panes. A space seeded from an arrangement
        // and not USING it would drift the moment anybody edited either.
        layoutId: currentLayoutId ?? undefined,
      }),
    [spaceLayer.createSpace, spaceLayout, currentLayoutId],
  )

  /**
   * Save what is open now as this layout's panes, or clear them (S6h).
   *
   * The only honest way to author a tree: a person arranges panes by dragging
   * them, so the arrangement they want is the one on screen. A pane editor in a
   * 288px panel would be a worse way to say the same thing.
   */
  const saveLayoutTree = useCallback(
    (save: boolean) => {
      const worldId = readWorldPointer()
      if (!worldId || !currentLayoutId) return
      void setLayoutTree(
        repo as never,
        worldId,
        currentLayoutId,
        save ? (encodeContent(tree) ?? undefined) : undefined,
      )
    },
    [repo, currentLayoutId, tree],
  )

  /**
   * Put THIS space back to the layout's panes (S6h).
   *
   * Separate from switching layout, and deliberately destructive-on-request
   * rather than automatic. Switching a layout must not throw away what you have
   * open; asking for its panes is a different sentence.
   */
  const applyLayoutTree = useCallback(() => {
    const next = layoutTree(spaceLayout)
    if (!next) return
    layout.setTree(decodeContent(next))
  }, [spaceLayout, layout.setTree])

  /**
   * One row per type anything installed can open (RFC 0284 S6m).
   *
   * Desktop's "Open With". It exists because an installed thread was a one-way
   * door: `handles` claimed sessions outright, so the only way back to the
   * shipped one was uninstalling. Several claimants is a supported state now,
   * and this is where a person picks between them.
   */
  const openWithRows = useMemo(
    () =>
      handledKeys(installedAppList).map((key) => {
        const choice = resolveHandler(installedAppList, key, worldHandlers)
        return {
          key,
          // The one key that is not a mime gets the word a person uses for it.
          label: key === 'agent' ? 'Sessions' : key,
          options: choice.claimants.map((a) => ({ id: a.id, name: a.name })),
          chosen: choice.app?.id,
          source: choice.source,
        }
      }),
    [installedAppList, worldHandlers],
  )

  const setHandlerChoice = useCallback(
    (key: string, appId: string | undefined) => {
      const worldId = readWorldPointer()
      if (!worldId) return
      void setHandler(repo as never, worldId, key, appId)
    },
    [repo],
  )

  /**
   * The apps that declare settings, for Settings (RFC 0284 S6g).
   *
   * A SECTION EACH, not a tab each. Nassar asked for tabs; the panel is 288
   * pixels wide and most apps declare two or three fields, so a tab strip would
   * cost more room than the controls it hides and would hide the one app you
   * came to change behind a click. Sections stack, scroll and stay searchable
   * by eye, which is what a strip of tabs would be buying you at four times the
   * width.
   */
  const appSettingSections = useMemo(
    () =>
      installedAppList
        .filter((a) => (a.settings ?? []).length > 0)
        .map((a) => ({
          appId: a.id,
          name: a.name,
          fields: (a.settings ?? []).map((spec) => ({
            ...spec,
            value: a.settingValues?.[spec.key],
          })),
        })),
    [installedAppList],
  )

  /**
   * Change one, in the catalog that actually holds the app (RFC 0295).
   *
   * The same `scope` lesson uninstalling learned: writing a World app's setting
   * into the project catalog would edit a map that never held the entry, and
   * the control would appear to do nothing.
   */
  const setSetting = useCallback(
    (appId: string, key: string, value: SettingValue | undefined) => {
      const app = installedAppList.find((a) => a.id === appId)
      if (!app) return
      const catalogId = app.scope === 'world' ? readWorldPointer() : projectId
      if (!catalogId) return
      void setAppSetting(repo as never, catalogId, appId, key, value)
    },
    [repo, installedAppList, projectId],
  )

  /**
   * Make a layout that empties the sidebar (RFC 0284 S4d).
   *
   * Deliberately the ONE thing this button makes, rather than an editor. It is
   * the arrangement the RFC exists for and the only one whose value is obvious
   * without a picker: a person who wants no sidebar can now have none. Choosing
   * between fills needs installed ones to choose between, which is S5.
   *
   * INSTALLED IN THE WORLD, not the project, because chrome is not a
   * repository's (RFC 0295).
   *
   * THE WAY BACK IS SETTINGS, and the description says so. An emptied slot
   * renders nothing, so ⌘B toggles a sidebar that is not there: the reveal
   * flag flips and no column appears. ⌘, still opens this panel, and "Built
   * in" is the first row in it, which is the only reason emptying the sidebar
   * is a safe thing to offer.
   */
  const makeLayout = useCallback(() => {
    const worldId = readWorldPointer()
    if (!worldId || !spaceLayer.spaceId) return
    void (async () => {
      const id = await installLayout(repo as never, worldId, {
        name: 'No sidebar',
        description: 'Panes only. Come back here with ⌘, to undo it.',
        slots: { sidebar: [] },
      })
      spaceLayer.setLayout(spaceLayer.spaceId as string, id)
    })()
  }, [repo, spaceLayer.spaceId, spaceLayer.setLayout])

  // FIRST RUN. Armed by a newly created World (see world.ts), and re-armed by
  // `?onboarding=1` — which is how the flow is reviewed or re-run once it has
  // been completed, since there is otherwise no way back into it.
  const [onboarding, setOnboarding] = useState(() => {
    if (new URLSearchParams(window.location.search).get('onboarding') === '1')
      restartOnboarding()
    return isOnboardingPending()
  })

  /**
   * Which projects this window reads, how each pane resolves against its own,
   * and where the next pane lands (RFC 0281 S1b/S2).
   *
   * One hook rather than four `useMemo`s strung through here, because they are
   * one dependency chain and the invariant that binds them — a pane resolves
   * against the project IT names, never the window's — is only testable when
   * something holds all of it at once. See `useProjectLayer`.
   */
  const { catalogs, resolveFile, rendered, activeProjectId } = useProjectLayer({
    tree,
    focusedId,
    fallbackProjectId: projectId,
    projectDoc,
  })

  /**
   * An app's skill, opened in the editor (RFC 0334 3g): what its header tile's
   * menu and its Packages menu do. Undefined for an app with no skill, so the
   * tile stays a picture.
   */
  const appSkillAction = useCallback(
    (appId: string) => {
      const skill = appSkillOf(windowSkills.skills, appId)
      return skill
        ? () =>
            layout.open({
              kind: 'document',
              projectId: activeProjectId,
              docId: skill.documentId,
            })
        : undefined
    },
    [windowSkills.skills, layout, activeProjectId],
  )

  /**
   * What each app pane says it is showing (RFC 0273 `surface.setSubject`),
   * keyed by pane AND document so a pane that moves on to another document does
   * not keep the last one's subject.
   */
  const [paneSubjects, setPaneSubjects] = useState<
    ReadonlyMap<string, string | null>
  >(() => new Map())
  const notePaneSubject = useCallback(
    (key: string, id: string | null) =>
      setPaneSubjects((prev) =>
        prev.has(key) && prev.get(key) === id
          ? prev
          : new Map(prev).set(key, id),
      ),
    [],
  )
  const paneHolds = useCallback(
    (l: ContentLeaf) => {
      if (l.content.kind !== 'document') return null
      const key = `${l.id} ${l.content.docId}`
      if (paneSubjects.has(key)) return paneSubjects.get(key) ?? null
      // NOT UNTIL THE PROJECT'S CATALOG IS HERE. On the first render after a
      // reload the tree is known and the apps are not, so an app's view reads
      // as a plain document and is handed to the session for good (an open
      // pins). Holding nothing for a frame costs nothing: the touch is written
      // when the catalog arrives.
      const catalog = catalogs.get(l.content.projectId)
      if (!catalog) return null
      // An app opened by name shows its list until it says otherwise, and its
      // view document is source, not something a session was handed.
      const docId = l.content.docId
      return catalog.apps.some((a) => a.viewDocId === docId) ? null : docId
    },
    [paneSubjects, catalogs],
  )
  // RFC 0297 4b: a document on screen beside a conversation is one that session
  // has in hand. Mounted here because this is where what is on screen is known.
  useOpenTouches(repo, tree, paneHolds)

  /** Every document in this project, for `mod.docs` (RFC 0284 S5c). */
  const shellDocuments = useMemo(
    // STAMPED with the project, which the catalog rows do not carry and
    // `window.open` requires. A fill has no other way to learn one.
    () =>
      projectDocuments(projectDoc).map((d) => ({
        ...d,
        projectId: activeProjectId,
      })),
    [projectDoc, activeProjectId],
  )

  /**
   * Every session the window can reach, for `mod.sessions` (RFC 0284 §4g).
   *
   * THE WORLD'S, not this project's. RFC 0290 lets a window hold several
   * projects, and a list scoped to whichever one the focused pane happens to
   * sit on would change under a fill for a reason it cannot see. Each
   * row already carries its own `projectId`, which is what `window.open`
   * requires.
   *
   * The same rows the cross-project inbox draws, so an installed sidebar and
   * the shipped one cannot disagree about what exists. Zero hydration: titles
   * and timestamps off each project's registry, nothing opened.
   */
  const shellSessions = useWorldSessions()
  /** Every unfiled session, uncapped and on a clock, for the World view's
   *  lines and the Space view's summary (RFC 0336 S10). */
  const activityRows = useWorldActivity()

  /**
   * Every space with the name a person sees: its own, or for a space nobody
   * named, the title of what it holds (`spaceLabel`). What the strip, the
   * sidebar and the agent tools all read, so a space an agent is told about by
   * name is the one on screen under that name.
   */
  const labelledSpaces = useMemo(
    () =>
      spaceLayer.spaces.map((sp) => ({
        ...sp,
        name: spaceLabel(sp, (kind, id) =>
          kind === 'document'
            ? shellDocuments.find((document) => document.id === id)?.title
            : shellSessions.find((session) => session.id === id)?.title,
        ),
      })),
    [spaceLayer.spaces, shellDocuments, shellSessions],
  )

  /**
   * The project a SESSION belongs to, by id (RFC 0281 S2).
   *
   * `activeProjectId` answers "where does a new thing go", and every path that
   * opened a session by id was using it to answer a different question: which
   * project this session is already in. Those agree only when you open a
   * session from a pane already sitting on its project. From anywhere else the
   * pane claimed the wrong one, the thread rendered anyway — a session document
   * is found by id — and back listed a project the session was never in.
   *
   * Falls back to `activeProjectId` at each call site, which is the old
   * behaviour and the right one for a session no project lists yet.
   */
  const projectOfSession = useSessionProject(projectId)

  const openSessionIds = useMemo(
    () =>
      leaves(rendered)
        .map((l) => (l.content.kind === 'agent' ? l.content.sessionId : null))
        .filter((id): id is string => id !== null),
    [rendered],
  )

  const openIds = useMemo(
    () =>
      leaves(rendered)
        .map((l) => (l.content.kind === 'document' ? l.content.docId : null))
        .filter((id): id is string => id !== null),
    [rendered],
  )
  const {
    noteOpened,
    removeOpened,
    openIds: recentIds,
    // Keyed by SPACE (RFC 0281 §7). Empty string until the space is resolved,
    // which reads and writes one throwaway key rather than branching a hook —
    // and a space always resolves within a tick of the document arriving.
  } = useOpenItems(spaceLayer.spaceId ?? '')
  useEffect(() => {
    for (const id of openIds) noteOpened(id)
  }, [openIds, noteOpened])
  // A session you are in is part of the working set too, and for the same
  // reason: however it was reached — a click, a link, a refresh, ⌘T — it lands
  // in Home the same way.
  useEffect(() => {
    for (const id of openSessionIds) noteOpened(id)
  }, [openSessionIds, noteOpened])

  /** Clicking a document in the sidebar opens it BESIDE the focused pane. */
  const openDocument = useCallback(
    (file: Subject, ownerProjectId?: string) =>
      layout.open({
        kind: 'document',
        projectId: ownerProjectId ?? activeProjectId,
        docId: file.id,
      }),
    [layout, activeProjectId],
  )

  const openDiscover = useCallback(() => {
    setPackagesOpen(false)
    const existing = leaves(tree).find(
      (pane) => pane.content.kind === 'discover',
    )
    if (existing) {
      if (layout.maximizedId && layout.maximizedId !== existing.id)
        layout.toggleMaximized()
      layout.showIn(
        existing.id,
        { kind: 'discover', lineageId: null },
        { replace: true },
      )
      layout.setFocusedId(existing.id)
      return
    }
    layout.open({ kind: 'discover', lineageId: null })
  }, [layout, tree])

  /**
   * "Change icon" (RFC 0332 3c): open Icons, then tell it which app.
   *
   * Icons declares a `select` view command (RFC 0328), so the window asks the
   * mounted view to open its editor on the app. The view mounts a moment after
   * the pane opens, so this waits for its controller, up to five seconds.
   */
  const iconsApp = useMemo(
    () => installedAppList.find((a) => a.dataType === 'iconset'),
    [installedAppList],
  )
  const changeIcon = useCallback(
    (app: AppSummary) => {
      if (!iconsApp) return
      openDocument(appAsFile(iconsApp))
      const started = Date.now()
      const tick = () => {
        const control = appControlsByDoc.current.get(iconsApp.viewDocId)
        if (control) {
          void control({ action: 'select', input: { appId: app.id } }).catch(
            () => {},
          )
          return
        }
        if (Date.now() - started < 5000) setTimeout(tick, 150)
      }
      tick()
    },
    [iconsApp, openDocument],
  )

  /** A modified link bypasses normal document reuse. Command-click makes a
   * real split; command-shift-click makes a space containing only the target. */
  const placeLinkedContent = useCallback(
    (content: ContentLeaf['content'], target: LinkOpenTarget = 'current') => {
      if (target === 'space') {
        spaceLayer.createSpace(undefined, {
          content: encodeContent(leaf(content)) ?? undefined,
        })
        return
      }
      layout.open(
        content,
        'right',
        target === 'split' ? { again: true } : undefined,
      )
    },
    [layout, spaceLayer.createSpace],
  )

  /** The apps modal (RFC 0280 S3). Chrome, like settings: one of it, and it is
   *  never a pane. Not persisted, because nobody wants to arrive back in a
   *  dialog they left open. */
  const [packagesOpen, setPackagesOpen] = useState(false)
  /** Bumped after an update is taken, so Packages asks the registry again. */
  const [updatesChecked, setUpdatesChecked] = useState(0)
  const packageUpdates = usePackageUpdates(
    installedAppList,
    packagesOpen,
    updatesChecked,
  )

  // ⌘K targets: everything you can be taken to. A document opens in a pane; a
  // session shows in the agent block you are in, or in a new one.
  const [commandOpen, setCommandOpenState] = useState(false)
  /** Opened by ⌘. (RFC 0336 S7): straight onto the World thread, no rows. */
  const [worldThreadOnly, setWorldThreadOnly] = useState(false)
  const setCommandOpen = useCallback((open: boolean) => {
    setCommandOpenState(open)
    if (!open) setWorldThreadOnly(false)
  }, [])
  /**
   * The World agent's session (RFC 0336 S5), read off the World. The daemon
   * makes it on first run; opening ⌘K makes it too, so the view works on a
   * device whose daemon has not got there yet.
   */
  const worldPointer = readWorldPointer()
  const [worldDocForAgent] = useDocument(
    (worldPointer ?? undefined) as AnyDocumentId | undefined,
  )
  const worldAgentId = worldAgentSessionId(worldDocForAgent)
  // What the person calls it (RFC 0336): its session's title.
  const [worldAgentDoc] = useDocument(
    (worldAgentId ?? undefined) as AnyDocumentId | undefined,
  )
  const worldName = worldAgentName(worldAgentDoc)
  /**
   * How a call follows a put-through (RFC 0336 S8). Defined further down,
   * where the strip's state is, and read through a ref by the agent view API
   * published above it.
   */
  const callFollowsRef = useRef<(sessionId: string) => void>(() => {})
  /** The put-through, defined where the strip's state is (RFC 0336 S8). */
  const putThroughRef = useRef<(sessionId: string) => void>(() => {})
  /** Read by the agent view API, which is built once and must not go stale. */
  const worldAgentIdRef = useRef(worldAgentId)
  worldAgentIdRef.current = worldAgentId
  /** Whether the World view is open, for the agent view API published above
   *  the state it reads. */
  const worldOpenRef = useRef(commandOpen)
  worldOpenRef.current = commandOpen
  // The window never creates the World agent's session: only the daemon does,
  // so two peers cannot each write a different one (RFC 0336 S5 review).
  /** The project the new-pane composer will start a session in (RFC 0290 S6).
   *  Null follows the window's project, which is what it should do until
   *  somebody says otherwise. */
  const [newPaneProject, setNewPaneProject] = useState<string | null>(null)
  /**
   * RFC 0336 S2. Every OTHER project the World lists, read from the catalogs
   * the sidebar already holds, so ⌘K finds work anywhere and not only in the
   * window's project. Documents and sessions only: an app or a "new …" action
   * belongs to the project you are in. `owner` is how a picked row opens in
   * its own project, since the row itself carries only an id.
   */
  const worldProjects = useWorldProjects()
  const otherProjects = useMemo(
    () => worldProjects.filter((p) => p.projectId !== projectId),
    [worldProjects, projectId],
  )
  const otherProjectDocs = useProjectDocs(
    useMemo(() => otherProjects.map((p) => p.projectId), [otherProjects]),
  )
  const elsewhere = useMemo(
    () =>
      targetsElsewhere(otherProjects, otherProjectDocs, (doc, id) => {
        // Resolved in ITS project, with the World's apps (RFC 0338 3g).
        const apps = installedApps(worldDocForAgent, doc)
        const ref = doc.documents?.[id]
        if (!ref) return undefined
        const dataType = projectString(ref.dataType)
        return dataType
          ? apps.find((a) => a.dataType === dataType)?.id
          : handlerFor(apps, String(ref.mime ?? ''), worldHandlers)?.id
      }),
    [otherProjects, otherProjectDocs, worldDocForAgent, worldHandlers],
  )

  const commandTargets = useMemo<CommandTarget[]>(() => {
    if (!projectDoc) return []
    const doc = projectDoc as ProjectDoc
    const apps = installedAppList
    const appInternal = new Set(
      apps.flatMap((a) => [a.viewDocId, a.skillDocId]),
    )
    // A DOCUMENT app is not a place you go, so it is not offered as one. What
    // it offers is making a record, next to the records themselves — the list
    // and the create action in the box that already indexes everything, rather
    // than on a separate screen that duplicates both.
    // AN APP WITH NO DATA TYPE HAS NO RECORDS TO MAKE. The Text editor is a
    // document app that edits plain documents, so `create:` with an empty type
    // minted an untyped instance at `/<slug>` that no editor claims, and the
    // new document could not be typed into. Opening the app itself goes through
    // DocumentView's NewDocumentGate, which makes an `Untitled.md` instead.
    const out: CommandTarget[] = apps.filter(isAppRow).map((a) =>
      a.presentation === 'document' && a.dataType
        ? {
            id: `create:${a.dataType}`,
            title: `New ${a.name.toLowerCase()}`,
            hint: a.description,
            kind: 'create' as const,
            appId: a.id,
          }
        : {
            id: a.viewDocId,
            title: a.name,
            hint: a.description,
            kind: 'app' as const,
            appId: a.id,
          },
    )
    const documents = projectDocuments(doc)
    const viewPaths = currentViewPaths(documents, apps)
    for (const d of documents) {
      // The SAME predicate the sidebar uses, imported rather than repeated:
      // this list and that one disagreeing is a bug nobody would think to look
      // for. A document app's instances belong here (RFC 0271).
      if (
        !isBrowsableDocument(
          d,
          apps,
          appInternal,
          viewPaths,
          !(worldPointer && !worldDocForAgent),
        )
      )
        continue
      // A record carries its app, so its row draws the app's tile (RFC 0332).
      // Or, for a plain file, the app that opens it (the Text editor for
      // markdown).
      const owner = d.dataType
        ? apps.find((a) => a.dataType === d.dataType)
        : handlerFor(apps, d.mime, worldHandlers)
      out.push({
        id: d.id,
        title: d.title,
        hint: d.path,
        kind: 'document',
        ...(owner ? { appId: owner.id } : {}),
      })
    }
    for (const rawId of doc.sessionIds ?? []) {
      const id = String(rawId)
      out.push({
        id,
        title: sessionTitleOf(doc.sessionMeta?.[id]) || 'Untitled session',
        kind: 'session',
      })
    }
    // The window's project first, so a tie in ranking favours where you are.
    out.push(...elsewhere.rows)
    out.push({
      id: 'discover',
      title: 'Discover packages',
      hint: 'Find and install from the registry',
      kind: 'discover',
    })
    return out
  }, [
    projectDoc,
    installedAppList,
    worldHandlers,
    elsewhere,
    worldPointer,
    worldDocForAgent,
  ])

  // Whether the focused pane is an agent block ON a conversation, which is what
  // Esc's second rung asks about.
  const focusedSession = useMemo(() => {
    const l = focusedId ? leafAt(rendered, focusedId) : null
    return l?.content.kind === 'agent' ? l.content.sessionId : null
  }, [focusedId, rendered])

  /**
   * Show a session: reveal the pane already on it, else show it where you are if
   * that is an agent block, else give it a pane. One helper, because ⌘K and the
   * sidebar must agree — and reveal comes first for the same reason it does for
   * documents: picking a session twice means "take me there", not "again".
   */
  const showSession = useCallback(
    (sessionId: string) => {
      const content = {
        kind: 'agent' as const,
        // THE SESSION'S project, not the window's. Reveal reads this too: a
        // pane opened from the inbox names the real project, so a click from
        // the sidebar only finds it when both name the same one.
        projectId: projectOfSession(sessionId) ?? activeProjectId,
        sessionId,
      }
      const shown = leafShowing(rendered, content)
      if (shown) {
        layout.setFocusedId(shown.id)
        return
      }
      const focusedLeaf = focusedId ? leafAt(rendered, focusedId) : null
      if (focusedLeaf?.content.kind === 'agent')
        layout.showIn(focusedLeaf.id, content)
      else layout.open(content)
    },
    [layout, focusedId, rendered, activeProjectId, projectOfSession],
  )

  /**
   * A url: opened as a pane where a webview can render it, and handed to the
   * browser where one cannot. Either way a url that is not http(s) opens
   * nothing — this string is user-typed and ends up as a webview's src.
   */
  const openWeb = useCallback(
    (url: string, target: LinkOpenTarget = 'current') => {
      const f = webFileFor(url)
      if (!f) return
      if (canEmbedWebPages())
        placeLinkedContent(
          {
            kind: 'document',
            projectId: activeProjectId,
            docId: f.id,
          },
          target,
        )
      else window.open(url, '_blank', 'noopener,noreferrer')
    },
    [placeLinkedContent, activeProjectId],
  )

  /**
   * A link the DESKTOP SHELL intercepted (RFC 0269 S6): Electron's main process
   * catches `window.open` and same-origin-violating navigations, and hands the
   * url back here as a DOM event rather than opening a window for it.
   *
   * An event rather than IPC because there is no preload: the shell loads the
   * deployed app over https, so main dispatches this by executing a one-line
   * script in this renderer. On the web client nothing ever fires it, and the
   * listener costs nothing.
   *
   * An agent's browser_open (RFC 0303) sends `{ url, place: 'split' }`, so a
   * second page gets its own pane instead of replacing the first.
   */
  useEffect(() => {
    const onOpenWeb = (e: Event) => {
      const detail = (e as CustomEvent<unknown>).detail
      if (typeof detail === 'string') openWeb(detail)
      else if (
        detail &&
        typeof (detail as { url?: unknown }).url === 'string' &&
        (detail as { place?: unknown }).place === 'split'
      )
        openWeb((detail as { url: string }).url, 'split')
    }
    window.addEventListener('mod:open-web', onOpenWeb)
    return () => window.removeEventListener('mod:open-web', onOpenWeb)
  }, [openWeb])

  /**
   * ⇧⌘W — the desktop shell's Close Pane, arriving as a DOM event.
   *
   * The accelerator is owned by the app menu (see main.ts): the browser keeps
   * ⌘W for its own tab and never delivers it to a page, so a keymap binding
   * would work in one client and silently not in the other. Main dispatches
   * this instead, and only the desktop client ever fires it. ⌘W closes the
   * window there (RFC 0343), and ⌘Q quits.
   *
   * The LAST pane closes the space. The last space stays, and the window never
   * closes from here.
   */
  useEffect(() => {
    const onClosePane = () => {
      if (leaves(rendered).length > 1 && focusedId) layout.close(focusedId)
      else if (spaceLayer.spaces.length > 1 && spaceLayer.spaceId)
        spaceLayer.deleteSpace(spaceLayer.spaceId)
    }
    window.addEventListener('mod:close-pane', onClosePane)
    return () => window.removeEventListener('mod:close-pane', onClosePane)
  }, [
    rendered,
    focusedId,
    layout,
    spaceLayer.spaces.length,
    spaceLayer.spaceId,
    spaceLayer.deleteSpace,
  ])

  /**
   * ⌘K's Enter on typed text: a new session, shown, with that text already sent.
   *
   * Sent rather than left as a draft, because "start a session with this" is
   * one intent and a composer sitting on unsent text is a second thing to do.
   * The pane is shown BEFORE the send resolves so the thread is on screen while
   * the run is queued; a send that fails leaves an empty session you can retype
   * into, which is the recoverable half of the two failure modes.
   */
  const captureMessageViewRef = useRef<
    (sessionId: string | null) => Promise<MessageViewContext>
  >(async () => ({ view: null, viewAttachments: [] }))

  const startSession = useCallback(
    async (
      text: string,
      /**
       * What the COMPOSER chose, when a composer is what sent this.
       *
       * ⌘K and the new-pane page are the same launcher with the real
       * composer (RFC 0333), so the model you picked in the box that sent the
       * message has to be the model that answers it; falling back to the
       * preference would silently ignore a choice made two inches away. A
       * caller with no composer passes nothing and gets the preference.
       */
      chosen?: { provider: string; model: string },
      skills?: string[],
      attachments?: AttachmentRef[],
      /** Which project's registry lists the session (RFC 0290 S6). */
      chosenProjectId?: string,
    ) => {
      const model = chosen ?? preferredComposerModel()
      const target = chosenProjectId || projectId
      // `startSessionWithMessage` rather than a body of its own. This used to be
      // the better of two copies — it carried the skills, the attachments, the
      // agent and the project, and the other one dropped all four — so closing
      // the pair meant moving THIS body into the shared function and calling it
      // (RFC 0284 S8d).
      const captured = await captureMessageViewRef.current(null)
      return startSessionWithMessage(
        repo,
        target,
        {
          text,
          requestedModel: model,
          ...(skills?.length ? { skills } : {}),
          ...(attachments?.length ? { attachments } : {}),
          ...(captured.viewAttachments.length
            ? { viewAttachments: captured.viewAttachments }
            : {}),
          ...(captured.view ? { view: captured.view } : {}),
        },
        (sessionId) => showSession(sessionId),
      ).then(
        // The new session's id is the shared function's answer, and it is kept:
        // a composer ignores it, but `routeComment` answers WHICH session took
        // a comment, and a comment that starts one has no other way to say.
        (sessionId) => sessionId,
        () => {
          // Best effort: the session (if it was made) is on screen and the text
          // can be retyped. Nothing here should throw into the render tree.
          return undefined
        },
      )
    },
    [repo, projectId, showSession],
  )

  /**
   * Whether THIS machine will give you a shell, and which ones it has running
   * (RFC 0276 §3).
   *
   * Availability is "did the local server answer" — there is no World read on
   * this path, deliberately, so a relay outage cannot take away the terminal on
   * the machine under your hands.
   *
   * Polled only while something that can OFFER a shell is on screen: a new-pane
   * page, or ⌘K. It used to include the sidebar, which listed shells and is
   * always there, so the probe was effectively always on — one localhost fetch
   * every five seconds for the lifetime of the window. The sidebar no longer
   * has a terminals section, so that cost goes with it, and the probe is back
   * to being paid for only while somebody might act on the answer.
   */
  const hasPicker = useMemo(
    () => leaves(rendered).some((l) => l.content.kind === 'picker'),
    [rendered],
  )
  const { probe: localTerminals } = useLocalTerminals(
    hasPicker || commandOpen,
    activeProjectId,
  )

  /**
   * The pane that asked for a shell, rather than for the list of them.
   *
   * "New terminal" has to MAKE one, and a terminal leaf cannot say so on its
   * own: the id is minted by the server, so until it answers the only leaf
   * there is to open is `terminalId: null`, which is the machine's list. Naming
   * the pane here is the difference between an action that does what it says
   * and one that lands you on a list with the same words on it.
   *
   * Deliberately NOT in the leaf, and so not in the url: "I just asked for
   * this" is true for one moment in one window, and a url that carried it would
   * spawn a shell every time someone opened the link.
   */
  const [autoStartPaneId, setAutoStartPaneId] = useState<string | null>(null)

  /**
   * Open a shell from outside a terminal pane: the sidebar, or ⌘K.
   *
   * Null means "make me one", which is why this goes through the same
   * autostart flag the new-pane page uses rather than a second mechanism —
   * the id does not exist until the server mints it.
   */
  /**
   * This machine's shells, as things ⌘K can reach (RFC 0276 S3).
   *
   * Kept apart from `commandTargets` rather than folded into it, because that
   * memo is over the project DOCUMENT — the catalog everyone in the project
   * shares. These come from a machine, they are different on your other laptop,
   * and they must not end up in the new-pane page's recents, which reads that
   * same list.
   */
  const terminalTargets = useMemo<CommandTarget[]>(() => {
    if (!localTerminals) return []
    return [
      {
        id: TERMINAL_CREATE_ID,
        title: 'New terminal',
        hint: localTerminals.shell.split('/').pop() ?? 'shell',
        kind: 'create' as const,
      },
      ...localTerminals.live.map((s) => ({
        id: `terminal:${s.id}`,
        title: s.title,
        hint: 'terminal',
        kind: 'terminal' as const,
      })),
    ]
  }, [localTerminals])

  const openTerminal = useCallback(
    (shellId: string | null) => {
      if (!localTerminals) return
      const paneId = layout.open({
        kind: 'terminal',
        deviceId: localTerminals.deviceId,
        terminalId: shellId,
        projectId: activeProjectId,
      })
      if (shellId === null) setAutoStartPaneId(paneId)
    },
    [layout, localTerminals, activeProjectId],
  )

  const paletteTargets = useMemo(
    () => [...commandTargets, ...terminalTargets],
    [commandTargets, terminalTargets],
  )

  const onPickTarget = useCallback(
    (t: CommandTarget) => {
      if (t.kind === 'discover') {
        openDiscover()
        return
      }
      if (t.kind === 'session') {
        showSession(t.id)
        return
      }
      if (t.kind === 'terminal') {
        openTerminal(t.id.slice('terminal:'.length))
        return
      }
      // Before the `create:` branch, which parses a DATA TYPE out of the id and
      // would go looking for a "terminal" document type to make an instance of.
      // A shell is not a record; it is the one create action here that makes a
      // process rather than a document.
      if (t.id === TERMINAL_CREATE_ID) {
        openTerminal(null)
        return
      }
      if (t.kind === 'create') {
        const dataType = t.id.slice('create:'.length)
        // Never an untyped record: see the create targets above.
        if (!dataType) return
        const doc = projectDoc as ProjectDoc | undefined
        if (!doc) return
        void openOrCreateInstance(
          repo,
          projectId,
          dataType,
          projectDocumentsOfType(doc, dataType),
        ).then((id) =>
          layout.open({
            kind: 'document',
            projectId: activeProjectId,
            docId: id,
          }),
        )
        return
      }
      // A row from another project opens in that project (RFC 0336 S2).
      layout.open({
        kind: 'document',
        projectId: elsewhere.owner.get(t.id) ?? activeProjectId,
        docId: t.id,
      })
    },
    [
      layout,
      showSession,
      repo,
      projectId,
      projectDoc,
      activeProjectId,
      openTerminal,
      openDiscover,
      elsewhere,
    ],
  )

  /**
   * Where a document or session an agent asks to open lives (RFC 0336 S3).
   * The window's project first, so nothing that opened before moves; then
   * every other project the World lists. An id nobody lists is refused, as
   * before, rather than drawn as an empty pane.
   */
  /** Another project's sessions, apart from its documents: `owner` holds
   *  both, and a session id is not a document to open. */
  const elsewhereSessionIds = useMemo(
    () =>
      new Set(
        elsewhere.rows.filter((r) => r.kind === 'session').map((r) => r.id),
      ),
    [elsewhere],
  )
  const ownerOfDocument = useCallback(
    (docId: string): string | null =>
      resolveFile(activeProjectId, docId) !== null
        ? activeProjectId
        : resolveFile(projectId, docId) !== null
          ? projectId
          : elsewhereSessionIds.has(docId)
            ? null
            : (elsewhere.owner.get(docId) ?? null),
    [resolveFile, activeProjectId, projectId, elsewhere, elsewhereSessionIds],
  )
  const isKnownSession = useCallback(
    // Read through the ref for the window's own project (see openSession);
    // another project's sessions come from the World's catalogs.
    (sessionId: string): boolean =>
      liveSessionIds.current.has(sessionId) ||
      elsewhereSessionIds.has(sessionId),
    [elsewhereSessionIds],
  )

  // The space each session was last seen in, for an agent whose pane has
  // closed: its opens go there rather than into the space on screen.
  const sessionSpaces = useRef(new Map<string, string>())
  useEffect(() => {
    noteSessionSpaces(
      sessionSpaces.current,
      spaceLayer.spaces,
      spaceLayer.spaceId,
      rendered,
    )
  }, [spaceLayer.spaces, spaceLayer.spaceId, rendered])

  // The door the desktop shell calls through (agentViewApi). The agent's tools
  // run in the daemon and cannot touch this window; the shell can reach both, so
  // it asks here rather than any of this being synced.
  useEffect(
    () =>
      publishAgentView({
        // RFC 0303 3e. Straight through to the web feature's store, which
        // draws what the shell says: the take-over latch is the shell's.
        noteWebPage: (webContentsId, state) =>
          noteWebPage(webContentsId, state),
        read: () => ({
          openDocId: openIds[0] ?? null,
          panes: leaves(rendered).map(describePane),
          layout: arrangementName(rendered),
          // RFC 0281 S6. By NAME, which is what a person says back.
          space:
            labelledSpaces.find((sp) => sp.spaceId === spaceLayer.spaceId)
              ?.name ?? null,
          spaces: labelledSpaces.map((sp) => sp.name),
          sessions: leaves(rendered).flatMap((l) =>
            l.content.kind === 'agent' && l.content.sessionId
              ? [l.content.sessionId]
              : [],
          ),
        }),
        open: (docId) => {
          if (docId === null) {
            if (focusedId) layout.close(focusedId)
            return true
          }
          /**
           * REFUSED, not approximated, and this cost somebody their pane.
           *
           * This opened a document leaf for any id at all. An id that resolves
           * to NOTHING — a record whose app keeps its own index, so its
           * instances are not openable (RFC 0271, #264) — then drew an empty
           * pane, and because a centre open REPLACES, whatever was there was
           * gone. Opening a review record destroyed the Review app's index, and
           * the tool reported success.
           *
           * `mod.window.open` has said this since S2 and follows it. The agent
           * path did not, which is the drift two adapters over one window are
           * supposed to avoid.
           */
          // `set_view` names a document and not a project. It opens in the
          // window's project when that lists it, else in whichever project
          // does (RFC 0336 S3), so an agent can open work from anywhere.
          const owner = ownerOfDocument(docId)
          if (!owner) return false
          // Beside, as the tool promises: a second document splits rather
          // than replacing the one the person was reading.
          layout.open({ kind: 'document', projectId: owner, docId }, 'right', {
            beside: true,
          })
          return true
        },
        openSession: (sessionId) => {
          // Read through a ref, not a captured list: several view calls can
          // arrive in one tick without a render between them, and a stale list
          // would refuse a session that exists.
          if (!isKnownSession(sessionId)) return false
          layout.open({
            kind: 'agent',
            projectId: projectOfSession(sessionId) ?? activeProjectId,
            sessionId,
          })
          return true
        },
        controlApp: async ({ documentId, ...request }) => {
          // By document: the pane showing it, in whichever space, driven
          // where it is. Every space's panes stay mounted, so a review can be
          // walked in the background and found at the right stop on return.
          if (documentId) {
            const control = appControlsByDoc.current.get(documentId)
            return control ? control(request) : false
          }
          if (!focusedId) return false
          const control = appControls.current.get(focusedId)
          if (!control) return false
          return control(request)
        },
        focusPane: (index) => {
          const pane = leaves(rendered)[index - 1]
          if (!pane) return false
          layout.setFocusedId(pane.id)
          return true
        },
        closePane: (index) => {
          const pane = leaves(rendered)[index - 1]
          if (!pane) return false
          layout.close(pane.id)
          return true
        },
        setLayout: (name) => {
          const preset = ARRANGE_PRESETS.find(
            (p) =>
              p.id === name.toLowerCase() ||
              p.name.toLowerCase() === name.toLowerCase(),
          )
          // Unknown names are refused rather than guessed at, so the tool can
          // tell the model what this client actually offers.
          if (!preset) return false
          layout.applyArrangement(preset)
          return true
        },
        setSpace: (name) => {
          const target = findSpaceByName(labelledSpaces, name)
          // Refused rather than guessed at, the same as an unknown layout: the
          // agent moving the window to the WRONG space is worse than telling it
          // there is no such space.
          if (!target) return false
          spaceLayer.switchTo(target.spaceId)
          return true
        },
        openForSession: ({ sessionId, docId, openSessionId }) => {
          // The World agent opening a session for you is a put-through: a
          // call on it follows (RFC 0336 S8). Only the World agent's: any
          // other session opening one must not move your call.
          // Not while you are talking to it in the World view: there the
          // session it started is a card in the thread, the pane opens beside
          // it, and the view stays open for the rest of the conversation.
          if (
            openSessionId &&
            sessionId === worldAgentIdRef.current &&
            !worldOpenRef.current &&
            isKnownSession(openSessionId)
          ) {
            // The same handover as picking the session's row (RFC 0336 S8):
            // the World view folds, the strip there talks to it, and a call
            // follows. Opening a pane alone left typing addressed to Mod.
            putThroughRef.current(openSessionId)
            return true
          }
          // What to place. A session opened for another session is checked
          // against the same live list `openSession` uses, so an unknown id is
          // refused here rather than becoming an empty pane in someone's space.
          const docOwner = docId ? ownerOfDocument(docId) : null
          const content: Parameters<typeof layout.open>[0] | null = docId
            ? docOwner
              ? { kind: 'document', projectId: docOwner, docId }
              : null
            : openSessionId && isKnownSession(openSessionId)
              ? {
                  kind: 'agent',
                  projectId: projectOfSession(openSessionId) ?? activeProjectId,
                  sessionId: openSessionId,
                }
              : null
          if (!content) return false
          // DECIDED BEFORE ANYTHING IS TOUCHED, and never by moving the window.
          // Every path that cannot place the pane returns false here, so the
          // tool falls back to a plain open instead of reporting a change
          // nobody could see — and the panes on screen are left exactly as they
          // were, because nothing has run yet.
          const plan = planOpenForSession({
            sessionId,
            what: content,
            windowTree: rendered,
            spaces: spaceLayer.spaces,
            currentSpaceId: spaceLayer.spaceId,
            lastSpaceId: sessionSpaces.current.get(sessionId) ?? null,
          })
          if (plan.at === 'nowhere') return false
          if (plan.at === 'window') {
            layout.open(content)
            return true
          }
          return spaceLayer.writeSpaceContent(plan.spaceId, plan.content)
        },
        openWebForSession: ({ sessionId, url }) => {
          // An agent's browser_open, by openForSession's rule: the page goes
          // to the space the session works in, and the window stays where it
          // is. False hands it back to the shell's plain open.
          if (!canEmbedWebPages()) return false
          const plan = planWebOpenForSession({
            sessionId,
            url,
            projectId: activeProjectId,
            windowTree: rendered,
            spaces: spaceLayer.spaces,
            currentSpaceId: spaceLayer.spaceId,
            lastSpaceId: sessionSpaces.current.get(sessionId) ?? null,
          })
          if (plan.at === 'nowhere') return false
          if (plan.at === 'window') {
            // A new pane beside what is here, as the plain open does.
            openWeb(url, 'split')
            return { at: 'window' }
          }
          if (!spaceLayer.writeSpaceContent(plan.spaceId, plan.content))
            return false
          return {
            at: 'space',
            space:
              labelledSpaces.find((sp) => sp.spaceId === plan.spaceId)?.name ??
              '',
          }
        },
        showsSession: (sessionId) =>
          treeHoldsSession(rendered, sessionId) ||
          (spaceLayer.spaceId !== null &&
            sessionHomeSpace(
              sessionId,
              spaceLayer.spaces,
              sessionSpaces.current.get(sessionId) ?? null,
            ) === spaceLayer.spaceId),
        createSpace: (name) => {
          const wanted = name.trim()
          if (!wanted) return false
          // An existing name is a switch, not a second space with the same
          // label: two spaces called "chrome" make every later `space:` call
          // ambiguous, and the agent addresses them by name.
          const existing = findSpaceByName(labelledSpaces, wanted)
          if (existing) {
            spaceLayer.switchTo(existing.spaceId)
            return existing.name
          }
          if (!newSpace(wanted)) return false
          return wanted
        },
        renameSpace: (from, to) => {
          const wanted = to.trim()
          if (!wanted) return false
          const target = findSpaceByName(labelledSpaces, from)
          if (!target) return false
          spaceLayer.renameSpace(target.spaceId, wanted)
          return true
        },
        closeSpace: (name) => {
          const target = findSpaceByName(labelledSpaces, name)
          if (!target) return false
          // The last space is not closable: deleting it would make the next
          // resolve mint a fresh "Main", which reads as a rename that lost the
          // tree. `deleteSpace` already refuses, so answer honestly instead of
          // reporting a close that did not happen.
          if (spaceLayer.spaces.length <= 1) return false
          spaceLayer.deleteSpace(target.spaceId)
          return true
        },
      }),
    [
      rendered,
      openIds,
      focusedId,
      layout,
      activeProjectId,
      projectOfSession,
      spaceLayer,
      labelledSpaces,
      newSpace,
      ownerOfDocument,
      isKnownSession,
      openWeb,
    ],
  )

  useProjectKeymap({
    toggleSidebar: layout.toggleSidebar,
    toggleComposer: layout.toggleComposer,
    // ⌘.: the World thread, for just talking (RFC 0336 S7). Again closes it.
    openWorldThread: () => {
      if (commandOpen && worldThreadOnly) {
        setCommandOpen(false)
        return
      }
      setWorldThreadOnly(true)
      setCommandOpenState(true)
    },
    // ⌘\ acts on the pane you are in, and only where there is a canvas: an
    // agent block has no side column to open.
    toggleCanvasSide: () => {
      const l = focusedId ? leafAt(rendered, focusedId) : null
      if (l?.content.kind === 'document') toggleSide(l.id)
    },
    // ⌘K is the launcher, rows and all, even over the World thread.
    openCommandBar: () => {
      setWorldThreadOnly(false)
      setCommandOpenState(true)
    },
    // ⌘⇧.: the World thread, on a call with the World agent (RFC 0336 S8).
    openWorldCall: () => {
      // A call is talking, so it opens on the thread, as ⌘. does.
      setWorldThreadOnly(true)
      setCommandOpenState(true)
      if (!worldAgentId) return
      const target = { repo, sessionId: worldAgentId, title: worldName }
      // A call already on hands back to the World agent rather than hanging
      // up for a second one: the same call, re-targeted (RFC 0336 S8).
      if (sessionCalls.current()) sessionCalls.retarget(target)
      else sessionCalls.start(target)
    },
    // ⌘P, beside ⌘, for Settings (RFC 0284 S6l). The sidebar button was the
    // only way in, and a layout can empty the sidebar.
    openPackages: () => setPackagesOpen(true),
    // ⌘D / ⌘⇧D — a new pane beside or under the one you are in.
    //
    // A FRESH session list rather than a copy of what is focused, which is the
    // terminal's meaning of the key: ⌘D in Ghostty gives you a new shell in the
    // same directory, not a second view of the one you were typing in. The
    // project carries over the same way the directory does, so a split from a
    // project's pane lands in that project and one from the inbox stays in the
    // inbox. `again` because this always splits: a session list is usually
    // already on screen somewhere, and revealing that one is not a split.
    splitPane: (direction) => {
      // The new pane is EMPTY: a new-tab page listing what you have been in,
      // whatever kind of thing it was. The session list was the wrong thing to
      // put here — it answers "what goes here" with sessions only — and so was
      // forcing the sidebar out, which changed a piece of chrome you had set
      // deliberately in order to answer a question about one pane. Whatever
      // you pick lands in this pane (see FILL in useLayout).
      layout.open(
        { kind: 'picker', projectId: activeProjectId },
        direction === 'down' ? 'bottom' : 'right',
        { again: true },
      )
    },
    // The ladder's second rung, in the new vocabulary: the focused agent block
    // goes back to the session list, keeping its place.
    backToSessionList: () => {
      const l = focusedId ? leafAt(rendered, focusedId) : null
      if (l?.content.kind === 'agent') {
        // Back to the list this block came from: its OWN project's, or the
        // cross-project inbox if that is where it started. Written as two
        // branches because `AgentContent` pairs a null project with a null
        // session deliberately, and a single object of `string | null` fits
        // neither half of that pair.
        // The registry first, for the same reason the back button asks it: the
        // list you go back to is the one this session is actually in, whatever
        // the pane was stamped with.
        const p =
          (l.content.sessionId
            ? projectOfSession(l.content.sessionId)
            : null) ?? l.content.projectId
        layout.showIn(
          l.id,
          p
            ? { kind: 'agent', projectId: p, sessionId: null }
            : { kind: 'agent', projectId: null, sessionId: null },
        )
      }
    },
    // Spaces by position (RFC 0281 §7). Out of range is a no-op rather than a
    // clamp: ⌘7 with four spaces means nothing, and landing on the fourth would
    // be a different space from the one that keystroke names elsewhere.
    goToSpace: (index) => {
      const target = spaceLayer.spaces[index]
      if (target) spaceLayer.switchTo(target.spaceId)
    },
    // ⌘T — a new space, which is the same action as the strip's `+`.
    newSpace,
    cycleSpace: (delta) => {
      const at = spaceLayer.spaces.findIndex(
        (s) => s.spaceId === spaceLayer.spaceId,
      )
      if (at === -1) return
      // WRAPS, because a strip you can walk off the end of makes you look at
      // where you are before every press.
      const count = spaceLayer.spaces.length
      const next = spaceLayer.spaces[(at + delta + count) % count]
      if (next) spaceLayer.switchTo(next.spaceId)
    },
    hasOpenSession: Boolean(focusedSession),
    toggleSettings: layout.toggleSettings,
    closeFile: () => focusedId && layout.close(focusedId),
    hasOpenFile: leaves(rendered).length > 1,
    toggleMaximized: layout.toggleMaximized,
  })

  /**
   * Which panes have their side column open, by leaf id.
   *
   * PER PANE, because the column is about the document in that pane: two
   * documents open side by side, one with its comments showing, is a thing
   * people do. TRANSIENT, like focus and for the same reason — it is where you
   * are right now rather than what you are working on, so it stays out of the
   * url and out of localStorage. A pane that closes takes its entry with it.
   */
  const [sideOpenIds, setSideOpenIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const toggleSide = useCallback((paneId: string) => {
    setSideOpenIds((prev) => {
      const next = new Set(prev)
      if (!next.delete(paneId)) next.add(paneId)
      return next
    })
  }, [])

  // Left-edge hover reveal of the hidden sidebar (transient, not persisted).
  const [revealed, setRevealed] = useState(false)
  const revealedRef = useRef(false)
  revealedRef.current = revealed
  const showT = useRef<number | undefined>(undefined)
  const hideT = useRef<number | undefined>(undefined)
  useEffect(() => {
    if (chrome.sidebar) {
      setRevealed(false)
      return
    }
    const onMove = (e: MouseEvent) => {
      if (e.clientX <= EDGE_PX && !revealedRef.current) {
        window.clearTimeout(hideT.current)
        window.clearTimeout(showT.current)
        showT.current = window.setTimeout(() => setRevealed(true), REVEAL_DELAY)
      }
    }
    window.addEventListener('mousemove', onMove)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.clearTimeout(showT.current)
      window.clearTimeout(hideT.current)
    }
  }, [chrome.sidebar])
  const scheduleHide = () => {
    window.clearTimeout(hideT.current)
    hideT.current = window.setTimeout(() => setRevealed(false), HIDE_DELAY)
  }
  const cancelHide = () => window.clearTimeout(hideT.current)

  /**
   * What fills the sidebar slot (RFC 0284 S3, S4b).
   *
   * Resolved, not imported, and now resolved AGAINST THE LAYOUT the space names
   * rather than by taking whatever registered first. Three answers, because
   * `slotFill` distinguishes three states: a silent layout falls back to the
   * built-in, an emptied slot renders nothing, and a layout naming ids gets
   * those in order.
   *
   * Still first-wins at the end, because a sidebar is filled once (§4a). What
   * changed is which list the first comes from.
   */
  /**
   * The installed apps, as slot resolution needs to see them.
   *
   * One list for every slot, because `fillSlot` filters by fill and
   * realm itself. `panels` is the catalog's name for slot fills,
   * generalised from RFC 0273's sections by S3 and not yet renamed on the wire.
   */
  const slotApps = useMemo(
    () =>
      installedAppList.map((a) => ({
        id: a.id,
        name: a.name,
        realm: a.realm,
        viewDocId: a.viewDocId,
        fills: a.panels,
        // S6g. Carried so the mount builds the settings capability without a
        // second trip to the catalog.
        settings: a.settings,
        settingValues: a.settingValues,
      })),
    [installedAppList],
  )

  const sidebarSlot = fillSlot('sidebar', sidebarFill, slotApps)[0]

  /**
   * The sessions a new pane offers: the LIVE ones, in the order they need you.
   *
   * The registry read is zero-hydration (titles and timestamps, nothing
   * opened); `useSessionStatuses` then opens the top rows to say what each is
   * doing, exactly as the session list does. Drawn above the header because
   * the space buttons read the same statuses (RFC 0330).
   */
  const { sessions: sessionRows } = useSessionList(projectId)
  /**
   * Which sessions exist, for the agent view to check an id against.
   *
   * A ref rather than a dependency, because the view API is published in an
   * effect ABOVE this line: naming `sessionRows` in that effect's dependency
   * array would read it before it is initialised. Read at call time it is also
   * the fresher answer.
   */
  const liveSessionIds = useRef<Set<string>>(new Set())
  useEffect(() => {
    liveSessionIds.current = new Set(sessionRows.map((r) => r.id))
  }, [sessionRows])
  const liveSessions = useMemo(
    // Filed sessions are done by definition. A page about what to do next
    // should not lead with what somebody has already finished.
    () => sessionRows.filter((r) => !isSessionDone(r)),
    [sessionRows],
  )
  /**
   * WHAT EACH SESSION IS DOING, registry first (RFC 0284 S8a).
   *
   * The registry answers most rows for the price of one document read, and
   * `useSessionStatuses` opens only what is left: a session no runtime has
   * reported on, or one whose claim went stale because its writer died. Until
   * every runtime writes the mirror, "what is left" is everything, which is
   * exactly today's behaviour.
   */
  const registryStatuses = useRegistryStatuses(projectDoc)
  /** Each space's sessions: its panes plus its sidebar Open list (RFC 0330). */
  const sessionsBySpace = useMemo(() => {
    // RFC 0336 S8: a session from another project can be in a space too.
    const known = new Set([
      ...sessionRows.map((r) => r.id),
      ...elsewhereSessionIds,
    ])
    return spaceSessions(
      spaceLayer.spaces,
      spaceLayer.spaceId,
      recentIds,
      loadOpenIds,
      (id) => known.has(id),
    )
  }, [
    spaceLayer.spaces,
    spaceLayer.spaceId,
    recentIds,
    sessionRows,
    elsewhereSessionIds,
  ])
  // WHICH ROWS PAY A READ (RFC 0330 3b): every space's sessions, so a space
  // button can say what its sessions are doing, then the picker's rows while
  // one is on screen. Current space first, so a cap hit drops the far ones.
  const hydratedStatuses = useSessionStatuses(
    repo,
    useMemo(
      () =>
        statusReadOrder(
          sessionsBySpace,
          spaceLayer.spaceId,
          hasPicker ? liveSessions.map((r) => r.id) : [],
          registryStatuses,
          STATUS_ROW_LIMIT,
        ),
      [
        sessionsBySpace,
        spaceLayer.spaceId,
        hasPicker,
        liveSessions,
        registryStatuses,
      ],
    ),
  )
  const newPaneStatuses = useMemo(() => {
    // THE REGISTRY WINS on a disagreement, and there is only one way to
    // disagree: a row was hydrated because its claim was stale, and a fresh
    // claim landed since. The newer answer is the registry's.
    const merged = new Map(hydratedStatuses)
    for (const [id, status] of registryStatuses) merged.set(id, status)
    return merged
  }, [hydratedStatuses, registryStatuses])

  /** What each space's button says about its sessions (RFC 0330). */
  const spaceActivityById = useMemo(
    () =>
      new Map(
        sessionsBySpace.map((sp) => [
          sp.spaceId,
          spaceActivity(sp.sessionIds, newPaneStatuses),
        ]),
      ),
    [sessionsBySpace, newPaneStatuses],
  )

  /** What is open in the space you are in: on screen, so no World line. */
  const hereSessionIds = useMemo(
    () =>
      new Set(
        sessionsBySpace.find((sp) => sp.spaceId === spaceLayer.spaceId)
          ?.sessionIds ?? [],
      ),
    [sessionsBySpace, spaceLayer.spaceId],
  )
  /**
   * What fills the header (RFC 0284 S6c).
   *
   * The same three lines as the sidebar, which is the point of doing a second
   * slot: if the header had needed a fourth, the registry would have been
   * shaped around the sidebar rather than around slots.
   *
   * The strip is only given HEIGHT when something is in it, further down. A
   * built-in that decides it has nothing to draw returns null, and a row of
   * empty pixels above the window would be a worse default than the one we
   * shipped.
   */
  const headerSlot = fillSlot('header', headerFill, slotApps)[0]

  /**
   * How tall the header is, and whether it is there at all (RFC 0284 S6d).
   *
   * The shell used to answer both, at 36 pixels and always. Neither was its
   * decision to make: a module is handed a box it cannot resize, so "a taller
   * strip" and "no strip until I reach for it" were unreachable from inside a
   * fill AND unreachable from Settings. The layout answers them now,
   * which is the RFC's claim applied to shape rather than to content.
   */
  const headerGeometry = slotGeometry(spaceLayout, 'header')
  const headerHeight = headerGeometry.size ?? HEADER_HEIGHT_PX
  const headerFloats = headerGeometry.reveal === 'hover'
  const headerReveal = useEdgeReveal(headerFloats && !!headerSlot, 'top')

  /**
   * The header's content, drawn once and placed twice.
   *
   * Pinned and floating differ only in where the node goes, so building it in
   * both branches would be two copies of the props list that could disagree.
   */
  // RFC 0307: the call, from any space, at the right end of the header.
  // On screen means a pane shows the session or the space's composer strip is
  // on it: either draws the call's own controls. A pane matches on the session
  // alone, since its project can be read before the registry knows it.
  const callPill = (
    <CallPill
      isOnScreen={(sessionId) =>
        leaves(rendered).some(
          (l) =>
            l.content.kind === 'agent' && l.content.sessionId === sessionId,
        ) ||
        (composerSlot?.kind === 'builtin' &&
          composerTarget?.id === sessionId) ||
        // The World view draws the World agent's call itself (RFC 0336 S8).
        (worldOpen && sessionId === worldAgentId)
      }
      onOpen={showSession}
    />
  )
  const headerContent = headerSlot ? (
    headerSlot.kind === 'installed' ? (
      <InstalledSlot app={headerSlot.app} />
    ) : (
      headerSlot.builtin.render({
        spacesAsTabs: chrome.spacesAsTabs,
        spaces: labelledSpaces,
        currentSpaceId: spaceLayer.spaceId,
        onSwitch: spaceLayer.switchTo,
        onReorder: spaceLayer.reorderSpace,
        onCreate: newSpace,
        onRename: spaceLayer.renameSpace,
        onDelete: spaceLayer.deleteSpace,
        spaceActivity: spaceActivityById,
        trailing: callPill,
      })
    )
  ) : null
  const headerNode = headerContent
  // The pill rides in the space tabs; anywhere they are not drawn (an
  // installed header, or no header at all) it floats over the panes instead.
  const pillInHeader = headerSlot?.kind === 'builtin' && headerContent !== null

  /**
   * A PINNED BUILT-IN SIZES ITSELF, an installed one is given a box.
   *
   * The space tabs have always chosen their own height and still do when the
   * layout says nothing, so switching a World to this changes nothing on
   * screen. A module has no height of its own: drawn into a box with none, it
   * renders into nothing at all.
   */
  const headerBoxed =
    headerSlot?.kind === 'installed' || headerGeometry.size !== undefined

  const sidebar = (floating: boolean) => {
    /**
     * An INSTALLED sidebar mounts; a built-in renders (RFC 0284 S6a).
     *
     * Two shapes, not one, and the difference is real rather than cosmetic. A
     * built-in is React and composes; an installed one is a module handed a
     * container and `mod.window`, which is what lets it be written by somebody
     * who does not have our component tree.
     */
    if (sidebarSlot?.kind === 'installed')
      return (
        <InstalledSlot
          app={sidebarSlot.app}
          slot="sidebar"
          projectId={projectId}
        />
      )
    return sidebarSlot?.builtin.render({
      projectId,
      spaces: chrome.spacesAsTabs ? [] : labelledSpaces,
      currentSpaceId: spaceLayer.spaceId,
      onSwitchSpace: spaceLayer.switchTo,
      onCreateSpace: newSpace,
      onRenameSpace: spaceLayer.renameSpace,
      onDeleteSpace: spaceLayer.deleteSpace,
      onReorderSpace: spaceLayer.reorderSpace,
      spaceActivity: spaceActivityById,
      floating,
      openFileId: openIds[0] ?? null,
      openSessionId: focusedSession,
      openIds: recentIds,
      onOpen: openDocument,
      onOpenSplit: (file, ownerProjectId) =>
        layout.open(
          {
            kind: 'document',
            projectId: ownerProjectId ?? activeProjectId,
            docId: file.id,
          },
          'right',
          { again: true },
        ),
      onOpenSession: showSession,
      onOpenSessionSplit: (id) =>
        layout.open(
          {
            kind: 'agent',
            projectId: projectOfSession(id) ?? activeProjectId,
            sessionId: id,
          },
          'right',
          { again: true },
        ),
      onCloseOpenItem: removeOpened,
      onCollapse: floating ? undefined : layout.toggleSidebar,
      onPin: floating ? layout.toggleSidebar : undefined,
      onOpenApps: () => setPackagesOpen(true),
      onOpenDiscover: openDiscover,
    })
  }

  const paneCount = leaves(rendered).length

  /**
   * The composer slot (RFC 0284 S8d): type without spending a pane first.
   *
   * The same four lines as the header, which is the third time and the point of
   * having a registry at all. What differs is only where the shell puts it and
   * what it hands over.
   */
  const composerSlot = fillSlot('composer', composerFill, slotApps)[0]
  const composerGeometry = slotGeometry(spaceLayout, 'composer')
  const composerHeight = composerGeometry.size ?? COMPOSER_HEIGHT_PX
  // Out of the way by the layout, or by ⌘J (`chrome.composerHidden`).
  const composerFloats =
    composerGeometry.reveal === 'hover' || chrome.composerHidden
  /**
   * WHAT IS ON SCREEN IN THIS SPACE, which is what the shipped composer decides
   * to draw on. `rendered` is the tree for the space on screen, so a thread open
   * in another space does not count: it is not here and cannot take what you
   * type.
   *
   * TWO ANSWERS, because the fill asks two questions. `openSessionIds`
   * (above, already computed for the sidebar) is the sessions a strip would be
   * duplicating a composer for; `spaceHasThread` is wider and includes a
   * session LIST pane, which offers to start one and holds no session id to
   * appear in the other list.
   *
   * A PICKER PANE COUNTS. The new-pane page is the other box in this app that
   * starts a session, and it is the same `Composer` with the same model picker
   * and the same `/` menu. A strip under it is a second empty box on a page
   * whose whole middle is already one, and neither is named, so nothing on
   * screen says which one you are meant to type in. It holds no session id, so
   * `openSessionIds` cannot see it, which is the reason this answer is wider.
   */
  //
  // A MAXIMIZED PANE HIDES THE REST (RFC 0305), so only it is on screen. A
  // thread hidden behind a maximized document cannot take what you type, and
  // counting it would take the strip away exactly when it is the only box.
  const onScreen = useMemo(
    () =>
      leaves(rendered).filter(
        (l) => !layout.maximizedId || l.id === layout.maximizedId,
      ),
    [rendered, layout.maximizedId],
  )
  const spaceHasThread = onScreen.some(
    (l) => l.content.kind === 'picker' || l.content.kind === 'agent',
  )
  const onScreenSessionIds = onScreen
    .map((l) => (l.content.kind === 'agent' ? l.content.sessionId : null))
    .filter((id): id is string => id !== null)

  /** One capture rule for both composers: a thread pane and the compact strip
   * can sit over the same work, so they must stamp the same window. */
  const messageViewFor = useCallback(
    (sessionId: string | null) =>
      viewManifest(
        onScreen.map((leaf) => ({
          content: leaf.content,
          focused: leaf.id === focusedId,
        })),
        {
          sessionId,
          space:
            labelledSpaces.find((space) => space.spaceId === spaceLayer.spaceId)
              ?.name ?? null,
          titleOf: (kind, id) =>
            kind === 'document'
              ? shellDocuments.find((document) => document.id === id)?.title
              : shellSessions.find((session) => session.id === id)?.title,
        },
      ),
    [
      onScreen,
      focusedId,
      labelledSpaces,
      spaceLayer.spaceId,
      shellDocuments,
      shellSessions,
    ],
  )
  // RFC 0307: the voice on a call sees the screen the agent is told about.
  useEffect(
    () => sessionCalls.setViewReader((sessionId) => messageViewFor(sessionId)),
    [messageViewFor],
  )
  // RFC 0336 S1: a spoken answer lands in the answered session's project.
  useEffect(
    () => sessionCalls.setProjectOf((id) => projectOfSession(id) ?? undefined),
    [projectOfSession],
  )
  // RFC 0336 3d: "back to World" on a call. The same call, put through to the
  // World agent, with its thread open so the screen says who is listening.
  useEffect(
    () =>
      sessionCalls.setBackToWorld(() => {
        if (!worldAgentId) return false
        if (sessionCalls.current()?.sessionId === worldAgentId) return false
        sessionCalls.retarget({
          repo,
          sessionId: worldAgentId,
          title: worldName,
        })
        setWorldThreadOnly(true)
        setCommandOpenState(true)
        return true
      }),
    [worldAgentId, repo, worldName],
  )
  /**
   * The pixel half of RFC 0304 3d: each visible document pane drawn by the page
   * at the instant Send is pressed. Terminals are left out: they paint with
   * WebGL, which a redraw cannot read, and the manifest already names them.
   */
  const captureMessageViewFor = useCallback(
    async (sessionId: string | null): Promise<MessageViewContext> => {
      const view = messageViewFor(sessionId)
      if (!view) return { view: null, viewAttachments: [] }
      const eligible = onScreen.flatMap((leaf, index) => {
        if (leaf.content.kind !== 'document') return []
        return [{ id: leaf.id, title: view.panes[index]?.title }]
      })
      try {
        const files = await capturePaneImages(eligible)
        const viewAttachments = await Promise.all(
          files.map(async (file) => {
            const source = await fileToSource(file)
            return attachFile(source, {
              processImage: browserProcessImage,
              repo: {
                create: (doc) => repo.create(doc).documentId as string,
              },
            })
          }),
        )
        return { view, viewAttachments }
      } catch {
        // A screenshot is additive context. The semantic manifest still makes
        // the send useful, and a local capture outage must never eat the draft.
        return { view, viewAttachments: [] }
      }
    },
    [messageViewFor, onScreen, repo],
  )
  captureMessageViewRef.current = captureMessageViewFor
  /**
   * A FLOATING COMPOSER STILL REVEALS WHEN A THREAD IS OPEN, and that is not
   * an oversight. The fill decides whether to draw; the edge decides
   * whether to offer. Suppressing the reveal here would move that decision back
   * into the shell for one of the two cases, which is exactly the split S8c
   * exists to avoid. An empty reveal costs a strip of background for a moment.
   */
  const composerReveal = useEdgeReveal(
    composerFloats && !!composerSlot,
    'bottom',
  )
  // ⌘J (RFC 0336 3c): out of the way, the composer still comes up while you
  // type in it, and while a session waits on your answer.
  const [composerFocused, setComposerFocused] = useState(false)

  /**
   * The one pane whose header offers to reveal the sidebar: the first agent or
   * empty pane in visual order, `leaves` being left to right and top to bottom.
   * Document panes carry no such button at all, so this is the nearest header
   * to the sidebar that has anywhere to put it.
   */
  const sidebarRevealPaneId =
    leaves(rendered).find(
      (l) =>
        l.content.kind === 'agent' ||
        l.content.kind === 'picker' ||
        l.content.kind === 'terminal',
    )?.id ?? null

  /**
   * WHAT A SESSION ROW SAYS ABOUT ITSELF, in two words or none.
   *
   * Shared by the new-pane page and the composer strip's menu, which are two
   * lists of the same sessions: one saying "waiting on you" and the other
   * saying nothing about the same session is the kind of disagreement that
   * makes a person distrust both. Unknown says NOTHING rather than inventing
   * "quiet": only "waiting on you" must never be guessed at.
   */
  const sessionNote = useCallback(
    (id: string) => {
      const s = newPaneStatuses.get(id)
      if (s === 'asking') return 'waiting on you'
      if (s === 'running' || s === 'queued') return 'working'
      return undefined
    },
    [newPaneStatuses],
  )

  /**
   * What a new pane offers: the working set, resolved against the live catalog.
   *
   * `recentIds` is the same list the sidebar's Home view shows, and it holds
   * documents and sessions together in the order they were reached — which is
   * exactly what a new-tab page wants. Anything not recent enough to be here is
   * one ⌘K away, and the header says so.
   */
  const newPaneItems = useMemo(() => {
    const byId = new Map(
      commandTargets.filter((t) => t.kind !== 'create').map((t) => [t.id, t]),
    )
    const recent: NewPaneItem[] = []
    const taken = new Set<string>()
    for (const id of recentIds) {
      const t = byId.get(id)
      if (!t || taken.has(id)) continue
      taken.add(id)
      recent.push({
        id,
        title: t.title,
        hint: t.hint,
        kind: t.kind === 'session' ? 'session' : 'document',
      })
      if (recent.length >= 8) break
    }
    // Conversations you are not in right now still belong on this page: a new
    // pane is very often "the other session", and it would be odd to have to
    // search for one that is right there in the list next door.
    //
    // Ranked by whether it needs you, then by recency — the session list's
    // grouping flattened into one column, because eight rows do not want four
    // headers. Unknown status sorts with idle rather than inventing "quiet":
    // only "waiting on you" must never be guessed at.
    const rank = (id: string) => {
      const s = newPaneStatuses.get(id)
      if (s === 'asking') return 0
      if (s === 'running' || s === 'queued') return 1
      return 2
    }
    const sessions: NewPaneItem[] = liveSessions
      .filter((r) => !taken.has(r.id))
      .sort(
        (a, b) =>
          rank(a.id) - rank(b.id) || (b.updatedAt ?? 0) - (a.updatedAt ?? 0),
      )
      .slice(0, 8)
      .map((r) => ({
        id: r.id,
        title: r.title,
        note: sessionNote(r.id),
        kind: 'session' as const,
      }))
    const tasks: NewPaneItem[] = unownedTasks(projectDoc)
      .slice(0, 8)
      .map((task) => ({
        id: task.id,
        title: task.title,
        note: 'open task',
        kind: 'task' as const,
      }))
    return { recent, sessions, tasks }
  }, [
    commandTargets,
    recentIds,
    liveSessions,
    newPaneStatuses,
    sessionNote,
    projectDoc,
  ])

  const startingTaskIds = useRef(new Set<string>())
  const startTask = useCallback(
    async (taskId: string) => {
      if (startingTaskIds.current.has(taskId)) return
      startingTaskIds.current.add(taskId)
      try {
        await startSessionWithMessage(
          repo,
          activeProjectId,
          {
            text: `Take task ${taskId}. Read the task first, then complete it to its acceptance criteria.`,
            requestedModel: preferredComposerModel(),
          },
          (sessionId) => showSession(sessionId),
          taskId,
        )
      } catch (error) {
        // Someone took it since the page drew it; the tile drops on its own.
        if (!(error instanceof TaskTaken)) throw error
      } finally {
        startingTaskIds.current.delete(taskId)
      }
    },
    [activeProjectId, repo, showSession],
  )

  /**
   * Everything the new-pane page's composer needs that is not the message.
   *
   * Assembled HERE rather than inside the panel, for the reason Composer's own
   * props give: a presentational box must be able to mount in a test or a slot
   * with no repo in context. The shell is what holds a repo, a World and the
   * project list, so the shell is what hands them over.
   */
  // The new-pane composer's `/` lists the skills of the project it will start
  // in (RFC 0334 S4), which may not be the window's.
  const [newPaneProjectDoc] = useDocument(
    (newPaneProject ?? activeProjectId) as AnyDocumentId,
  )
  const { skills: newPaneSkills } = useSkills(newPaneProjectDoc)
  const projectHosting = useProjectHosting()
  /**
   * The harness catalogue, for the composers that belong to no session yet.
   *
   * `ThreadView` and the session list read the same hook. A session's box was
   * once the only place it existed, which is why the new-pane page and the
   * chrome strip offered Mod and nothing else. The picker reads its harnesses
   * OUT of the devices list (`resolveComposerHarnessGroups`), so a composer
   * handed no devices cannot show a harness, and the menu silently collapses to
   * the one built-in.
   *
   * Placement is NOT passed with it, and that is the difference from the thread.
   * `selectedDeviceId` is a session's chosen worker; there is no session here
   * yet, so the device menu stays hidden and the start picks one.
   */
  const composerDevices = useComposerDevices()

  // RFC 0307: the new pane's voice button, while it makes the session.
  const [newPaneVoice, setNewPaneVoice] = useState<{
    starting: boolean
    error?: string
  }>({ starting: false })
  const newPaneComposer = useMemo(
    () => ({
      devices: composerDevices,
      /** Attachments need only a repo, not a session, so a message can carry a
       *  file before the session that will hold it exists (RFC 0288). */
      onAttach: async (file: File): Promise<AttachmentRef> => {
        const source = await fileToSource(file)
        return attachFile(source, {
          processImage: browserProcessImage,
          repo: { create: (doc) => repo.create(doc).documentId as string },
        })
      },
      skills: newPaneSkills,
      projects: projectChoices(worldProjects, projectHosting),
      projectId: newPaneProject ?? activeProjectId,
      onSelectProject: setNewPaneProject,
      // RFC 0307: no session yet, so the voice button makes one, opens it,
      // and the call starts in that session's own composer.
      voice: voiceBeforeSession(
        newPaneVoice,
        () => {
          const target = newPaneProject ?? activeProjectId
          // One session per press: a second click while it is being made
          // would make a second "Voice call".
          if (!target || newPaneVoice.starting) return
          setNewPaneVoice({ starting: true })
          void createSession(repo, target, {
            title: 'Voice call',
            titleSetBy: 'stub',
          }).then(
            (handle) => {
              const sessionId = handle.documentId as string
              startVoiceOnOpen(sessionId)
              showSession(sessionId)
              setNewPaneVoice({ starting: false })
            },
            (err: unknown) =>
              setNewPaneVoice({
                starting: false,
                error: `Voice could not start: ${err instanceof Error ? err.message : String(err)}`,
              }),
          )
        },
        () => setNewPaneVoice({ starting: false }),
      ),
    }),
    [
      repo,
      composerDevices,
      newPaneSkills,
      worldProjects,
      projectHosting,
      activeProjectId,
      newPaneProject,
      showSession,
      newPaneVoice,
    ],
  )

  /**
   * WHAT THE COMPOSER STRIP CAN TALK TO: this space's sessions, most recently
   * reached for first (RFC 0284 S8d).
   *
   * The working set, not the panes. A session can be the one you were last in
   * with no pane holding it, and that is precisely the case the strip exists
   * for; `window.read()` cannot answer it and `recentIds` can, because it is
   * what has been reached for IN THIS SPACE rather than what is on screen now.
   *
   * Resolved against the live catalog, like every other reader of that list: an
   * id in neither list has been deleted, and offering to message it would be a
   * row that sends nowhere.
   */
  /** The strip can talk to a session in any project the World lists, once
   *  you have been put through to it (RFC 0336 S8). */
  const stripSessions = useMemo(
    () => [
      ...liveSessions,
      ...elsewhere.rows
        .filter((r) => r.kind === 'session')
        .map((r) => ({ id: r.id, title: r.title })),
    ],
    [liveSessions, elsewhere],
  )
  const composerTargets = useMemo(
    () => sessionsInReach(recentIds, stripSessions, sessionNote),
    [recentIds, stripSessions, sessionNote],
  )

  /**
   * Which of them it is talking to.
   *
   * `undefined` is the DEFAULT, held apart from an explicit choice rather than
   * seeded into the state. Seeding would freeze the first answer the working
   * set gave, which on a cold window is "nothing yet": the strip would offer a
   * new session forever while the space filled up with threads behind it.
   * Undefined re-reads, so the default stays "the one you were last in".
   *
   * PER SPACE (RFC 0336 S6). Each space remembers who you were talking to, so
   * switching spaces swaps the strip's recipient, visibly, to that space's
   * agent, and coming back restores the one you left. A choice is never
   * carried into another space: the session you picked there is not in this
   * one, and carrying it over would send this space's typing there.
   */
  const [choiceBySpace, setChoiceBySpace] = useState<
    Record<string, string | null>
  >({})
  // A window with no space yet keys its choice under '', like one space.
  const composerSpaceId = spaceLayer.spaceId ?? ''
  const composerChoice: string | null | undefined =
    composerSpaceId in choiceBySpace
      ? choiceBySpace[composerSpaceId]
      : undefined
  const setComposerChoice = useCallback(
    (id: string | null | undefined) =>
      setChoiceBySpace((prev) => {
        const next = { ...prev }
        if (id === undefined) delete next[composerSpaceId]
        else next[composerSpaceId] = id
        return next
      }),
    [composerSpaceId],
  )

  /**
   * PUT THROUGH (RFC 0336 3d, S8). The World view folds away, the window moves
   * to the space that already has the session (or stays put), and the strip
   * there talks to it directly. Nothing is relayed: the next words typed go to
   * that session as they are. No line says so: the session chip names who
   * hears the next message, which is all a put-through changes.
   */
  const putThroughTo = useCallback(
    (sessionId: string) => {
      // A session the strip cannot talk to (one filed as done) opens instead,
      // as picking it always did.
      if (!stripSessions.some((r) => r.id === sessionId)) {
        setCommandOpen(false)
        showSession(sessionId)
        return
      }
      // Where it is ACTIVE, not where you are: a space whose composer was
      // talking to it counts, though it was never a pane there.
      const home = homeSpaceOf(sessionId, {
        currentSpaceId: spaceLayer.spaceId,
        chosen: choiceBySpace,
        spaces: spaceLayer.spaces,
        sessionsBySpace,
      })
      const spaceId = home ?? composerSpaceId
      const moving = home !== null && home !== spaceLayer.spaceId
      if (moving) spaceLayer.switchTo(home)
      setChoiceBySpace((prev) => ({ ...prev, [spaceId]: sessionId }))
      // Noted where it LANDS. The switch lands a render later, so a note
      // through the hook would file the session under the space being left;
      // a move writes the destination's list directly instead, at the top, so
      // that space's composer has it in reach when the switch arrives.
      if (moving && home) noteOpenedIn(home, sessionId)
      else noteOpened(sessionId)
      setCommandOpen(false)
      callFollowsRef.current(sessionId)
    },
    [
      sessionsBySpace,
      choiceBySpace,
      composerSpaceId,
      spaceLayer,
      noteOpened,
      stripSessions,
      showSession,
      setCommandOpen,
    ],
  )
  putThroughRef.current = putThroughTo
  /**
   * A call follows a put-through only when it is on the World agent: a call
   * you started on a focused session stays with it.
   */
  callFollowsRef.current = (sessionId: string) => {
    const call = sessionCalls.current()
    if (!call || !worldAgentId || call.sessionId !== worldAgentId) return
    const title =
      stripSessions.find((r) => r.id === sessionId)?.title ?? 'that session'
    const projectIdOf = projectOfSession(sessionId) ?? undefined
    sessionCalls.retarget({
      repo,
      sessionId,
      ...(projectIdOf ? { projectId: projectIdOf } : {}),
      title,
    })
  }
  const composerTargetId =
    composerChoice === undefined
      ? (composerTargets[0]?.id ?? null)
      : composerChoice
  /** Resolved HERE, so the box and the send cannot disagree: a chosen session
   *  that has fallen out of the working set is a new session to both. */
  const composerTarget =
    composerTargets.find((t) => t.id === composerTargetId) ??
    // An EXPLICIT choice is honoured past the suggestion cap: a put-through to
    // a session ninth in a space's list must not land on an empty box that
    // starts a new session. Only a remembered default is limited to what is
    // in reach.
    (composerChoice
      ? (() => {
          const row = stripSessions.find((r) => r.id === composerChoice)
          return row
            ? { id: row.id, title: row.title, note: sessionNote(row.id) }
            : null
        })()
      : null)
  /**
   * The Space view's line about EVERYWHERE ELSE (RFC 0336 S10). Leaves out
   * what this space shows and, always, the session this composer talks to:
   * its own activity is drawn right under the summary, and counting it again
   * there drew the same work twice, blinking as its claim refreshed.
   */
  const composerTargetIdForSummary = composerTarget?.id ?? null
  const elsewhereSummary = useMemo(
    () =>
      worldSummary(activityRows, {
        worldAgentId,
        exclude: composerTargetIdForSummary
          ? new Set([...hereSessionIds, composerTargetIdForSummary])
          : hereSessionIds,
      }),
    [activityRows, worldAgentId, hereSessionIds, composerTargetIdForSummary],
  )
  /** A session waits on your answer: its own, or one the summary names. The
   *  composer is where you answer, so ⌘J does not hide it then. */
  const composerAsking =
    (composerTarget !== null &&
      newPaneStatuses.get(composerTarget.id) === 'asking') ||
    (elsewhereSummary?.asking.length ?? 0) > 0
  // The strip's view of the window's call (RFC 0307), for its target session.
  // Called every render, hooks being hooks; unused when there is no target.
  const stripVoice = useSessionVoice({
    repo,
    sessionId: composerTarget?.id ?? '',
    ...((composerTarget && projectOfSession(composerTarget.id)) ||
    activeProjectId
      ? {
          projectId: String(
            (composerTarget && projectOfSession(composerTarget.id)) ||
              activeProjectId,
          ),
        }
      : {}),
    sessionTitle: composerTarget?.title ?? 'this session',
  })
  // The World view's composer draws the World agent's call (RFC 0336 S8), so
  // a call opened with ⌘⇧. shows where its conversation is.
  const worldVoice = useSessionVoice({
    repo,
    sessionId: worldAgentId ?? '',
    sessionTitle: worldName,
  })

  /**
   * Where a comment made in this space goes (RFC 0301 §3d): the strip's
   * target, resolved above, and the strip's own way of starting a thread when
   * there is none. Handed down as one value so the box and the delivery agree.
   */
  const spaceSession = useMemo(
    () => ({
      sessionId: composerTarget?.id ?? null,
      start: async (text: string) =>
        (await startSession(
          text,
          undefined,
          undefined,
          undefined,
          newPaneProject ?? undefined,
        )) ?? null,
    }),
    [composerTarget?.id, startSession, newPaneProject],
  )

  /**
   * The composer strip's content, drawn once and placed twice, like the header.
   *
   * `onAsk` is the shell's own start and send, not a second implementation of
   * either. That is the whole of the write question S8d poses: a first message
   * that skips the title, or goes to a different model than the composer would
   * have chosen, or lands in a project nothing hosts, is an inconsistency
   * nobody thinks to report as a bug. Both paths end in
   * `thread/startSession.ts`, which is now the only copy of either.
   *
   * CONTINUING DOES NOT OPEN A PANE, and that is the point rather than an
   * omission. Spending a pane is the cost S8d exists to avoid, and a strip that
   * opened one on every send would have charged it anyway. The message is
   * queued, the menu's row says "working" on the next tick, and the document
   * you were reading stays where it is.
   */
  const composerNode = composerSlot ? (
    composerSlot.kind === 'installed' ? (
      <InstalledSlot app={composerSlot.app} />
    ) : (
      composerSlot.builtin.render({
        target: composerTarget,
        sessions: composerTargets,
        onSelectTarget: setComposerChoice,
        openSessionIds: onScreenSessionIds,
        hasThread: spaceHasThread,
        onAsk: async (text, model, skills, attachments) => {
          if (composerTarget) {
            // RFC 0304 3h: while the agent waits on a question, what you type
            // is the answer. Sent as a message it would queue behind the very
            // question it answers.
            if (
              !attachments?.length &&
              (await answerPendingWithText(
                repo,
                projectOfSession(composerTarget.id) ?? activeProjectId,
                composerTarget.id,
                text,
              ))
            ) {
              noteOpened(composerTarget.id)
              return
            }
            // Rejects on failure, which is what puts the draft back. The strip
            // shows no thread, so a send that vanished silently would be
            // invisible in a way the same failure in a thread is not.
            const captured = await captureMessageViewFor(composerTarget.id)
            await sendToSession(
              repo,
              composerTarget.id,
              projectOfSession(composerTarget.id) ?? activeProjectId,
              {
                text,
                requestedModel: model,
                skills,
                attachments,
                // RFC 0304 3d: the strip sits over the work, so what is on
                // screen is the "this" in what was typed. The panes actually
                // showing, so a maximized pane stands alone.
                ...(captured.view ? { view: captured.view } : {}),
                ...(captured.viewAttachments.length
                  ? { viewAttachments: captured.viewAttachments }
                  : {}),
              },
            )
            // REACHING FOR A SESSION, which is what the working set records.
            // Every other way of reaching one opens a pane and gets this for
            // free; this one deliberately does not, so without the call the
            // session you have been talking to all afternoon would sink down
            // the list that decides which one you are talking to.
            noteOpened(composerTarget.id)
            return
          }
          await startSession(
            text,
            model,
            skills,
            attachments,
            newPaneProject ?? undefined,
          )
        },
        // RFC 0307: pointed at a session, the strip carries THAT session's
        // call. With the new pane's settings alone its voice button was the
        // new pane's, so closing the thread made a live call look ended, and
        // pressing it started a new session and hung the real call up.
        composer: composerTarget
          ? {
              ...newPaneComposer,
              voice: stripVoice,
              // The strip is a composer on a live session, so it says when
              // that session's agent is working, as the thread's does: Stop
              // for the turn, not an idle box that reads as stopped.
              busy: ['running', 'queued'].includes(
                String(newPaneStatuses.get(composerTarget.id) ?? ''),
              ),
              onStop: async () => {
                await cancelRun(repo, composerTarget.id)
              },
            }
          : newPaneComposer,
        // RFC 0297: what the session this strip is talking to has in hand.
        ...(composerTarget
          ? {
              onOpenTarget: () =>
                layout.open({
                  kind: 'agent',
                  projectId:
                    projectOfSession(composerTarget.id) ?? activeProjectId,
                  sessionId: composerTarget.id,
                }),
              activity: (
                <>
                  <WorldSummary
                    summary={elsewhereSummary}
                    onOpen={() => {
                      setWorldThreadOnly(true)
                      setCommandOpenState(true)
                    }}
                  />
                  <SessionActivity
                    key={composerTarget.id}
                    sessionId={composerTarget.id}
                    projectId={
                      projectOfSession(composerTarget.id) ?? activeProjectId
                    }
                  />
                </>
              ),
              holdings: (line: { leading: ReactNode; trailing: ReactNode }) => (
                <SessionHoldingsBar
                  key={composerTarget.id}
                  sessionId={composerTarget.id}
                  projectId={
                    projectOfSession(composerTarget.id) ?? activeProjectId
                  }
                  onOpenDocument={(docId) =>
                    layout.open({
                      kind: 'document',
                      projectId: activeProjectId,
                      docId,
                    })
                  }
                  framed={false}
                  leading={
                    <>
                      {line.leading}
                      {projectDoc &&
                      (projectOfSession(composerTarget.id) ??
                        activeProjectId) === projectId ? (
                        <SessionTasksChip
                          key={`tasks-${composerTarget.id}`}
                          repo={repo}
                          projectId={projectId}
                          projectDoc={projectDoc}
                          sessionId={composerTarget.id}
                          showGoal
                          onOpenDocument={(docId) =>
                            layout.open({
                              kind: 'document',
                              projectId: activeProjectId,
                              docId,
                            })
                          }
                        />
                      ) : null}
                    </>
                  }
                  trailing={line.trailing}
                />
              ),
            }
          : // A space with no session yet still says what the rest of the
            // World is doing (RFC 0336 S10): the summary is not a session's.
            {
              activity: (
                <WorldSummary
                  summary={elsewhereSummary}
                  onOpen={() => {
                    setWorldThreadOnly(true)
                    setCommandOpenState(true)
                  }}
                />
              ),
            }),
      })
    )
  ) : null

  /** A built-in sizes itself, an installed one is given a box. The header's
   *  rule, for the header's reason: a module drawn into a container with no
   *  height renders into nothing. A layout that names a `size` is asking for a
   *  box too, and gets one. */
  const composerBoxed =
    composerSlot?.kind === 'installed' || composerGeometry.size !== undefined

  /*
   * `newSession` used to live here: an empty session titled "Session N", for
   * the new pane's New session row. Both are gone. The row is a `create` target
   * like every other kind now, and starting one from this page goes through
   * `startSession`, which sends the text you typed — a session that begins with
   * a message beats one that begins with a counter for a name and an empty
   * composer waiting for you to say the thing you already had in mind.
   */

  /**
   * WHO DRAWS A SESSION (RFC 0284 §4b).
   *
   * Resolved, not named. Today the answer is always the built-in thread, so
   * nothing changes on screen; what changes is that a session leaf now asks the
   * same question a document leaf has always asked, through the same function.
   *
   * Hoisted out of `renderPane` rather than resolved inside it. The lookup reads
   * the catalog, and doing that in the callback would make `renderPane` depend
   * on the whole project document, so every pane in the window would be
   * rebuilt on every sync tick that touched anything at all.
   *
   * An app that claims `handles: ['agent']` is FOUND here and cannot be mounted
   * yet: that is S6's second half, gated on S2, because a thread renderer needs
   * to open a document, split a pane and switch a session, and none of those
   * cross the bridge until `mod.window` exists.
   */
  const thread = useMemo(
    () =>
      resolveRenderer(
        'agent',
        AGENT_RENDERER_KEY,
        installedAppList,
        worldHandlers,
      ),
    [installedAppList, worldHandlers],
  )

  /** WHAT goes in a pane. ContentPanes owns where. */
  /**
   * Send to the World agent (RFC 0336 S7). No project, because its session is
   * in none; the panes on screen ride along, so "this" means what you are
   * looking at. Rejecting puts the draft back, as the strip's send does:
   * nothing typed is lost while the daemon starts.
   */
  const askWorld = useCallback(
    async (
      text: string,
      model: { provider: string; model: string },
      skills?: string[],
      attachments?: AttachmentRef[],
    ) => {
      if (!worldAgentId) {
        throw new Error(
          'The World agent is starting on this device. Try again in a moment.',
        )
      }
      // While Mod waits on a question, what you type is the answer, as it is
      // in the strip (RFC 0304 3h): sent as a message it would queue behind
      // the very question it answers.
      if (
        !attachments?.length &&
        (await answerPendingWithText(repo, undefined, worldAgentId, text))
      )
        return
      const captured = await captureMessageViewRef.current(null)
      await sendToSession(repo, worldAgentId, undefined, {
        text,
        requestedModel: model,
        ...(skills?.length ? { skills } : {}),
        ...(attachments?.length ? { attachments } : {}),
        ...(captured.view ? { view: captured.view } : {}),
        ...(captured.viewAttachments.length
          ? { viewAttachments: captured.viewAttachments }
          : {}),
      })
    },
    [worldAgentId, repo],
  )

  const renderPane = useCallback(
    (pane: ContentLeaf) => {
      /**
       * WHICH PANE IS ACTIVE follows the pointer as well as the caret.
       *
       * `onFocus` alone only fires for the focusABLE things inside a pane — a
       * composer, a button — so clicking a thread to read it, or a document's
       * body, left "active" wherever it happened to be. Every keybinding that
       * acts on the focused pane (⌘D's split, ⌘\, Esc) then acted somewhere
       * else, which reads as the shortcut being wrong rather than the pane
       * being the wrong one.
       *
       * Pointer-DOWN, captured, so it lands before anything inside can stop the
       * event, and before the click it belongs to does its own work.
       */
      const focusProps = {
        'data-mod-pane-id': pane.id,
        onFocus: () => layout.setFocusedId(pane.id),
        onPointerDownCapture: () => layout.setFocusedId(pane.id),
      }
      if (pane.content.kind === 'discover') {
        return (
          <div className="flex h-full w-full" {...focusProps}>
            <Panel
              focused={focusedId === pane.id}
              header={
                <>
                  <span className="min-w-0 flex-1 truncate font-medium text-xs">
                    Discover
                  </span>
                  <PanelAction
                    title="Open in split — a second Discover pane"
                    onClick={() =>
                      layout.open(pane.content, 'right', { again: true })
                    }
                  >
                    <Columns2 size={14} />
                  </PanelAction>
                  <PanelAction
                    title="Close Discover (esc)"
                    onClick={() => layout.close(pane.id)}
                  >
                    <X size={14} />
                  </PanelAction>
                </>
              }
            >
              <DiscoverPage
                lineageId={pane.content.lineageId}
                installedApps={installedAppList}
                onSelect={(lineageId) =>
                  layout.showIn(pane.id, { kind: 'discover', lineageId })
                }
                onInstall={async (release, releaseId, scope) => {
                  const worldId = readWorldPointer()
                  if (scope === 'world' && !worldId)
                    throw new Error('No World is open for this install')
                  await installRelease(
                    repo as never,
                    {
                      projectId: activeProjectId,
                      worldId: worldId ?? undefined,
                      scope,
                    },
                    release,
                    releaseId,
                  )
                }}
                onAsk={async (text) => {
                  await askWorld(text, preferredComposerModel())
                  setWorldThreadOnly(true)
                  setCommandOpen(true)
                }}
              />
            </Panel>
          </div>
        )
      }
      if (pane.content.kind === 'picker') {
        return (
          <div className="flex h-full w-full" {...focusProps}>
            <NewPanePanel
              recent={newPaneItems.recent}
              sessions={newPaneItems.sessions}
              tasks={newPaneItems.tasks}
              // Straight through `open`, which puts it HERE: this pane is the
              // focused one, and an empty pane takes what you pick next.
              onPick={(item) => {
                if (item.kind === 'task') {
                  void startTask(item.id)
                  return
                }
                layout.open(
                  item.kind === 'session'
                    ? {
                        kind: 'agent',
                        projectId: projectOfSession(item.id) ?? activeProjectId,
                        sessionId: item.id,
                      }
                    : {
                        kind: 'document',
                        projectId: activeProjectId,
                        docId: item.id,
                      },
                )
              }}
              // The SAME list ⌘K ranks, so the box on this page and the palette
              // never disagree about what this World holds — including the
              // `create:` entries, which is how New session and New terminal
              // survive losing their hard-coded rows.
              targets={paletteTargets}
              onPickTarget={onPickTarget}
              // The model, skills and files the composer chose ride along with
              // the text: they are decisions about THIS message, and the box
              // that made them is two inches from the one that sends it.
              onAsk={(text, model, skills, attachments) =>
                void startSession(
                  text,
                  model,
                  skills,
                  attachments,
                  newPaneProject ?? undefined,
                )
              }
              onOpenUrl={openWeb}
              // RFC 0336: the row under Ask sends to the World agent, and its
              // thread opens so the answer has somewhere to land.
              worldName={worldName}
              onAskWorld={async (text, model, skills, attachments) => {
                await askWorld(text, model, skills, attachments)
                setWorldThreadOnly(true)
                setCommandOpenState(true)
              }}
              composer={newPaneComposer}
              onSearch={() => setCommandOpen(true)}
              onClose={paneCount > 1 ? () => layout.close(pane.id) : undefined}
              sidebarCollapsed={!chrome.sidebar}
              onShowSidebar={
                pane.id === sidebarRevealPaneId
                  ? layout.toggleSidebar
                  : undefined
              }
              focused={focusedId === pane.id}
            />
          </div>
        )
      }
      if (pane.content.kind === 'agent') {
        const sessionId = pane.content.sessionId
        /**
         * Read out here, not in the callback: the narrowing to an agent block
         * does not survive into a closure.
         *
         * NULL IS KEPT, not defaulted. A null project is the cross-project
         * inbox (RFC 0281 §6), which is a different list from "this window's
         * project", and collapsing the two would make the inbox unreachable.
         */
        const paneProjectId = pane.content.projectId
        if (thread.source === 'none') return null
        const threadProps = {
          projectId: activeProjectId,
          listProjectId: paneProjectId,
          selectedSessionId: sessionId,
          // RFC 0304 3d: a full thread pane may sit beside the same document
          // as the compact strip. Both send paths stamp the same window; the
          // transcript still renders only what the person typed.
          messageView: messageViewFor(sessionId),
          captureMessageView: () => captureMessageViewFor(sessionId),
          // `/` in the composer can write a skill; this is what lets it open
          // the document it just wrote (RFC 0283 S5).
          // In the project that lists it: a document shared into a session
          // is in the guest's General, not the window's project (RFC 0345).
          onOpenDocument: (docId: string) =>
            layout.open({
              kind: 'document' as const,
              projectId: ownerOfDocument(docId) ?? activeProjectId,
              docId,
            }),
          // The back button, and picking from the list: one block changing
          // what it shows, keeping its place, size and focus.
          //
          // The row's OWN project, which the inbox reports and a single-project
          // list does not need to. Then the registry, then this block's.
          //
          // BACK (a null id) asks the registry about the session being LEFT,
          // and that is the heal: a pane stamped with the wrong project before
          // this fix — or restored from a url or a saved space that carries the
          // old stamp — corrects itself at exactly the moment the wrong list
          // would otherwise appear.
          onSelect: (id: string | null, rowProjectId?: string | null) =>
            layout.showIn(pane.id, {
              kind: 'agent' as const,
              projectId:
                rowProjectId ??
                projectOfSession(id ?? sessionId ?? '') ??
                paneProjectId ??
                activeProjectId,
              sessionId: id,
            }),
          sidebarCollapsed: !chrome.sidebar,
          // Only on the pane nearest the sidebar. It reveals one piece of
          // chrome down the left edge, so one header owns it — repeated in
          // every pane it read as a per-pane control. The FIRST pane that can
          // carry it rather than strictly the leftmost, so a window whose left
          // pane is a document still offers the button somewhere; ⌘B works
          // from anywhere regardless.
          onShowSidebar:
            pane.id === sidebarRevealPaneId ? layout.toggleSidebar : undefined,
          onSplit: () => layout.open(pane.content, 'right', { again: true }),
          // Only when there is somewhere to go back to. Closing the last pane
          // leaves an empty window, which the tree immediately refills with a
          // fresh agent block — a button that appears to do nothing.
          onClose: paneCount > 1 ? () => layout.close(pane.id) : undefined,
          focused: focusedId === pane.id,
        }
        return (
          <div className="flex h-full w-full" {...focusProps}>
            {thread.source === 'builtin' ? (
              thread.renderer.render(threadProps)
            ) : (
              // An installed app claims `agent`, so it draws the session (S6b).
              // The leaf's OWN session and project, not the window's: two
              // threads side by side is the ordinary case, and that is the
              // whole difference between a renderer and a chrome slot.
              <ThreadSlot
                app={thread.app}
                sessionId={sessionId ?? null}
                projectId={paneProjectId ?? null}
              />
            )}
          </div>
        )
      }
      if (pane.content.kind === 'terminal') {
        const { deviceId, terminalId } = pane.content
        const termProjectId = pane.content.projectId ?? activeProjectId
        return (
          <div className="flex h-full w-full" {...focusProps}>
            <TerminalPanel
              projectId={termProjectId}
              deviceId={deviceId}
              terminalId={terminalId}
              // Only for the pane that just asked for a shell, and only until
              // it has one: the panel disarms itself on the first start, so the
              // back button reaches the list rather than bouncing off it.
              autoStart={pane.id === autoStartPaneId}
              // The back button, picking a shell, and the id a newly started
              // shell comes back with: one block changing what it shows,
              // keeping its place, size and focus.
              onSelect={(id) =>
                layout.showIn(pane.id, {
                  kind: 'terminal',
                  deviceId,
                  terminalId: id,
                  projectId: termProjectId,
                })
              }
              sidebarCollapsed={!chrome.sidebar}
              onShowSidebar={
                pane.id === sidebarRevealPaneId
                  ? layout.toggleSidebar
                  : undefined
              }
              // A shell beside a shell: the machine's list, so the split is a
              // place to start something rather than a second view of the pty
              // you are typing in.
              onSplit={() =>
                layout.open(
                  {
                    kind: 'terminal',
                    deviceId,
                    terminalId: null,
                    projectId: termProjectId,
                  },
                  'right',
                  {
                    again: true,
                  },
                )
              }
              onClose={paneCount > 1 ? () => layout.close(pane.id) : undefined}
              focused={focusedId === pane.id}
            />
          </div>
        )
      }
      // This pane's OWN project, read out before the JSX: everything below is
      // about the document in THIS pane, and a second pane may be on another
      // project entirely (RFC 0281 S2).
      const paneProject = pane.content.projectId
      const paneDocId = pane.content.docId
      const file = resolveFile(paneProject, paneDocId)
      if (!file) return null
      // RFC 0273: if this pane is showing an app's view, the app declares the
      // sections its side column offers. Resolved per PANE, since a second pane
      // may be on a different app entirely — and against that pane's project,
      // because an app installed in one project is not installed in another.
      // On appId, not viewDocId: a rebuilt app must not detach mid-session.
      const app = catalogs
        .get(paneProject)
        ?.apps.find((a) => a.id === file.appId)
      return (
        <div className="flex h-full w-full" {...focusProps}>
          <CanvasPanel
            file={file}
            app={app}
            onSubjectChange={(id) =>
              // The LEAF's document, which is what `paneHolds` keys on. Not
              // `file.id`: an app opened by name resolves to the app's id.
              notePaneSubject(`${pane.id} ${paneDocId}`, id)
            }
            // The document's project, not the window's: the side column reads
            // comments and the document catalog off the project that HOLDS it.
            projectId={paneProject}
            breadcrumbs={fileBreadcrumbs(file, {
              sidebarCollapsed: !chrome.sidebar,
              agentCollapsed: false,
            })}
            focused={focusedId === pane.id}
            tabs={paneTabs(pane.content)}
            onSelectTab={(docId) => layout.showTab(pane.id, docId)}
            onOpenTab={(docId) => layout.openTab(pane.id, docId)}
            onCloseTab={(docId) => layout.closeTab(pane.id, docId)}
            sideOpen={sideOpenIds.has(pane.id)}
            onToggleSide={() => toggleSide(pane.id)}
            onClose={() => layout.close(pane.id)}
            onSplit={() => layout.open(pane.content, 'right', { again: true })}
            // Each agent block has its own composer now, so there is nothing to
            // portal and no collapsed panel to reveal.
            agentCollapsed={false}
            onShowAgent={() =>
              layout.open({
                kind: 'agent',
                projectId: activeProjectId,
                sessionId: null,
              })
            }
            composerSlotRef={null}
            // RFC 0273: a view handing a document to the host, the same way it
            // hands over a session. It opens in a pane beside this one, and an
            // id the catalog does not know simply does not open. One found by
            // a World search (mod.world) opens in the project that holds it.
            onOpenDocument={(id, target) =>
              placeLinkedContent(
                {
                  kind: 'document',
                  projectId:
                    resolveFile(paneProject, id) !== null
                      ? paneProject
                      : (ownerOfDocument(id) ?? paneProject),
                  docId: id,
                },
                target,
              )
            }
            onOpenUrl={openWeb}
            // The pane follows its page. Replacing, not pushing, so the
            // window's back button is not every link clicked inside the page;
            // and the row Home kept for the old url goes, so one pane is one
            // row there rather than one per page it has been on.
            onWebNavigate={(url) => {
              const f = webFileFor(url)
              if (!f || f.id === paneDocId) return
              removeOpened(paneDocId)
              layout.showIn(
                pane.id,
                { kind: 'document', projectId: paneProject, docId: f.id },
                { replace: true },
              )
            }}
            appSkillAction={appSkillAction}
            onAppControl={(controller) => {
              if (controller) appControls.current.set(pane.id, controller)
              else appControls.current.delete(pane.id)
              // By document too, so "Change icon" can find the Icons view it
              // just opened without knowing which pane it landed in.
              if (controller)
                appControlsByDoc.current.set(paneDocId, controller)
              else appControlsByDoc.current.delete(paneDocId)
            }}
            onOpenSession={(id) =>
              layout.open({
                kind: 'agent',
                projectId: projectOfSession(id) ?? activeProjectId,
                sessionId: id,
              })
            }
          />
        </div>
      )
    },
    [
      paneCount,
      sidebarRevealPaneId,
      newPaneItems,
      notePaneSubject,
      removeOpened,
      // No `workspaceId` and no `workspaceDoc`: every pane now reads the
      // project it names (RFC 0281 S2), so the window's own is not an input to
      // rendering one.
      chrome.sidebar,
      focusedId,
      layout,
      resolveFile,
      catalogs,
      sideOpenIds,
      toggleSide,
      activeProjectId,
      projectOfSession,
      thread,
      appSkillAction,
      autoStartPaneId,
      // Without this the empty pane keeps the answer it was first rendered
      // with — which is "no terminal server", since the probe has not come
      // back yet — and the New terminal row never appears. It rides in on
      // `paletteTargets` now rather than as its own prop, but the reason to
      // re-render is unchanged.
      paletteTargets,
      onPickTarget,
      startSession,
      newPaneComposer,
      newPaneProject,
      openWeb,
      placeLinkedContent,
      ownerOfDocument,
      startTask,
      messageViewFor,
      captureMessageViewFor,
      setCommandOpen,
      askWorld,
      repo,
      worldName,
      installedAppList,
    ],
  )

  const worldOpen = commandOpen

  /** ⌘K's World view, drawn in the strip's place (RFC 0336 S7). */
  const worldViewNode = (
    <WorldView
      open={worldOpen}
      threadOnly={worldThreadOnly}
      worldAgentSessionId={worldAgentId}
      targets={paletteTargets}
      onClose={() => setCommandOpen(false)}
      // A session row puts you through to it (S8); anything
      // else opens as it always did.
      onPickTarget={(t) =>
        t.kind === 'session' ? putThroughTo(t.id) : onPickTarget(t)
      }
      onOpenUrl={openWeb}
      // New session: the same composer and project as the
      // new-pane page, so the model you pick here answers.
      onAsk={(text, model, skills, attachments) =>
        void startSession(
          text,
          model,
          skills,
          attachments,
          newPaneProject ?? undefined,
        )
      }
      // RFC 0336 S7: Return asks the World agent. No project,
      // because its session is in none; the panes on screen
      // ride along, so "this" means what you are looking at.
      onAskWorld={askWorld}
      // RFC 0336 S10: where your agents are, as lines; one picked puts you
      // through. Every space's, this one's too: the World view is the whole
      // picture, and only the Space view's summary leaves out what is here.
      sessions={activityRows}
      onPutThrough={putThroughTo}
      // Stop for the World agent's turn, as a session's strip has.
      onStop={
        worldAgentId ? () => void cancelRun(repo, worldAgentId) : undefined
      }
      // The World agent's thread as a pane, the same as the strip's expand.
      onExpand={
        worldAgentId
          ? () => {
              setCommandOpen(false)
              showSession(worldAgentId)
            }
          : undefined
      }
      // ⌫ then ↵ on a line, or its check twice (RFC 0286): filed sessions
      // leave the lines when the registry says so.
      onFileDone={(sessionId, sessionProject) =>
        void setSessionCompleted(repo, sessionProject, sessionId, true)
      }
      onRename={
        worldAgentId
          ? (name) => void renameWorldAgent(repo, worldAgentId, name)
          : undefined
      }
      composer={
        worldAgentId
          ? { ...newPaneComposer, voice: worldVoice }
          : newPaneComposer
      }
    />
  )

  return (
    <AppIconsProvider apps={installedAppList} editorFor={editorFor}>
      {/* What `@` in any composer can name: the list ⌘K opens from, so the
          two agree on what exists (RefPicker). */}
      <ComposerRefs.Provider value={commandTargets}>
        <SelectionProvider>
          <WindowProvider
            /**
             * `mod.window`, from the shell's real state (RFC 0284 S2b).
             *
             * Wraps everything, so a fill rendered in any slot or any leaf
             * can ask for the window rather than be handed eleven commands as props.
             * That is what makes an installed fill possible at all: the shell
             * cannot pass props to something it has never heard of.
             */
            state={{
              tree,
              focused: focusedId,
              space: spaceLayer.spaceId ?? null,
              // RFC 0284 S5c. A sidebar's job is to show you what you HAVE, and the
              // window only says what is open. Without this a fill cannot
              // list a single document, which is the first thing anybody asks one
              // for.
              documents: shellDocuments,
              // §4g. The other half of what a person works on. Without it a
              // fill can see the sessions OPEN IN PANES and cannot see that
              // you have any others, so "a header with tabs for my sessions" was
              // half-answerable.
              sessions: shellSessions,
              // S4c. The built-in sidebar's Home view, which a replacement could not
              // draw. Device-local and capped, as `useOpenItems` argues it should be.
              recents: recentIds,
              writeTree: layout.setTree,
              setFocus: layout.setFocusedId,
              switchSpace: spaceLayer.switchTo,
            }}
          >
            <ComposerSlotProvider value={{ variant: 'panel', element: null }}>
              <SpaceSessionProvider value={spaceSession}>
                <div className="ws-next relative flex h-screen min-h-0 flex-col bg-background text-foreground">
                  {onboarding && (
                    <OnboardingFlow
                      repo={repo}
                      projectId={activeProjectId}
                      onDone={() => {
                        setOnboarding(false)
                        // Land on the composer, not an empty session list
                        // (RFC 0337 S6): the first move after setup is to type.
                        if (focusedId)
                          layout.showIn(focusedId, {
                            kind: 'picker',
                            projectId: activeProjectId,
                          })
                      }}
                    />
                  )}
                  {/* THE HEADER SLOT (RFC 0284 S6c), whose built-in is the space tabs
            (RFC 0281 S5a). OUTSIDE the row below, so the strip spans the window
            rather than starting after the sidebar: a space is the frame
            everything else is inside, and a tab bar indented past a column
            reads as belonging to the column.

            The built-in draws itself and decides its own height, exactly as it
            did before the slot existed. An installed one is a MOUNT, which
            needs a box: a module drawn into a container with no height has
            nowhere to render, so the shell gives the strip one. */}
                  {headerFloats ? (
                    /* OUT OF THE FLOW, which is the whole point of `hover`: the panes
              get the space back, and the strip floats over them when the
              pointer reaches the top edge. Keeping it in the flow and merely
              emptying it would leave the row of pixels behind, which is the
              limitation this slice exists to remove. */
                    headerReveal.revealed && (
                      // biome-ignore lint/a11y/noStaticElementInteractions: hover affordance; the controls inside are the interactive elements
                      <div
                        onMouseEnter={headerReveal.cancelHide}
                        onMouseLeave={headerReveal.scheduleHide}
                        style={{ height: headerHeight }}
                        className="absolute inset-x-1.5 top-1.5 z-30 overflow-hidden rounded-md border border-border/60 bg-background shadow-lg"
                      >
                        {headerNode}
                      </div>
                    )
                  ) : headerBoxed ? (
                    <div
                      style={{ height: headerHeight }}
                      className="shrink-0 overflow-hidden border-border/60 border-b"
                    >
                      {headerNode}
                    </div>
                  ) : (
                    headerNode
                  )}
                  <div className="flex min-h-0 flex-1">
                    {chrome.sidebar && (
                      <>
                        <div
                          style={{ width: chrome.sidebarWidth }}
                          className="shrink-0 overflow-hidden"
                        >
                          {sidebar(false)}
                        </div>
                        <ResizeHandle
                          ariaLabel="Resize sidebar"
                          onDelta={layout.nudgeSidebarWidth}
                        />
                      </>
                    )}

                    {/* THE PANES AND THE COMPOSER STRIP UNDER THEM (S8c).
                A column, so the strip spans the panes and stops at the
                settings drawer beside them: a composer running under an open
                Settings panel would be a box wider than the thing it belongs
                to. `relative` is what the floating variant positions against,
                so a revealed strip covers the panes rather than the window. */}
                    {/* min-w-0: this column is a flex item in a row, and without it its
                  minimum width is its content's. A breadcrumb truncates inside
                  its pane but still reports its full text as min-content, so one
                  long title widened every pane in the space past the window. */}
                    <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
                      {/* One ACTIVE tree only. A hidden space keeps its persisted
                  layout and stable leaf ids in useLayout, but not live session
                  handles, terminals, sandboxed iframes, or project catalogs.
                  Mounting every space made a cold launch deserialize every old
                  conversation and app at once, starving the renderer before a
                  person could use the space they actually opened. */}
                      <ContentPanes
                        key={spaceLayer.spaceId ?? ''}
                        tree={rendered}
                        renderPane={renderPane}
                        onResize={layout.resize}
                        onMove={layout.move}
                        maximizedId={layout.maximizedId}
                        onRestore={layout.toggleMaximized}
                      />
                      {/* RFC 0307: a voice call outlives the pane it started
                  in. Its pill lives in the header; with no header it floats
                  over the panes instead, out of the flow like the strip. */}
                      {pillInHeader ? null : (
                        <div className="pointer-events-none absolute top-2 right-3 z-40">
                          {callPill}
                        </div>
                      )}
                      {/* ALWAYS OUT OF THE FLOW (S8d). The strip used to be given
                  height, which pushed the document above it up: a composer that
                  resizes the pane is a smaller version of the pane it was
                  supposed to save. It overlays instead, and what is on screen
                  stays exactly where it was.

                  This is not the same question as edge reveal, which decides
                  WHEN it appears. `composerFloats` gates the hover behaviour;
                  displacing nothing is unconditional.

                  POINTER EVENTS OFF on the container, on again for the box
                  inside it: the overlay spans the panes, and a transparent
                  full-width strip that swallowed clicks would make the bottom
                  of every document unclickable. */}
                      {/* The World view IS the strip, opened upward: same
                          place, same frame, never a second box over it.
                          BOTH stay mounted and only one shows (RFC 0336 3c),
                          so each keeps its unsent draft, refs and files
                          across ⌘K and Esc. */}
                      <div
                        className={`pointer-events-none absolute inset-x-0 bottom-0 z-30 [&>*]:pointer-events-auto ${
                          worldOpen ? '' : 'hidden'
                        }`}
                      >
                        {worldViewNode}
                      </div>
                      {/* MOUNTED WHILE HIDDEN, so a draft survives ⌘J and
                          the edge: out of the way is a class, not an
                          unmount. */}
                      {composerNode ? (
                        // biome-ignore lint/a11y/noStaticElementInteractions: hover affordance; the controls inside are the interactive elements
                        <div
                          onMouseEnter={composerReveal.cancelHide}
                          onMouseLeave={composerReveal.scheduleHide}
                          onFocus={() => setComposerFocused(true)}
                          onBlur={(e) => {
                            // Still inside: focus moved between its controls.
                            if (
                              e.currentTarget.contains(
                                e.relatedTarget as Node | null,
                              )
                            )
                              return
                            setComposerFocused(false)
                          }}
                          className={`pointer-events-none absolute inset-x-0 bottom-0 z-30 [&>*]:pointer-events-auto ${
                            worldOpen ||
                            !composerShows({
                              floats: composerFloats,
                              revealed: composerReveal.revealed,
                              focused: composerFocused,
                              asking: composerAsking,
                            })
                              ? 'hidden'
                              : ''
                          }`}
                        >
                          {composerBoxed ? (
                            <div
                              style={{ height: composerHeight }}
                              className="mx-1.5 mb-1.5 overflow-hidden rounded-md border border-border/60 bg-background shadow-lg"
                            >
                              {composerNode}
                            </div>
                          ) : (
                            composerNode
                          )}
                        </div>
                      ) : null}
                    </div>

                    {chrome.settings && (
                      <SettingsModal onClose={layout.toggleSettings}>
                        <SettingsPanel
                          arrangement={arrangementName(rendered)}
                          chrome={chrome}
                          onArrange={layout.applyArrangement}
                          onToggleSidebar={layout.toggleSidebar}
                          onToggleSpacesAsTabs={layout.toggleSpacesAsTabs}
                          layouts={layoutChoices}
                          currentLayoutId={currentLayoutId}
                          onPickLayout={(id) =>
                            spaceLayer.spaceId &&
                            spaceLayer.setLayout(spaceLayer.spaceId, id)
                          }
                          onMakeLayout={makeLayout}
                          panes={{
                            saved: layoutTree(spaceLayout) !== undefined,
                            onSave: saveLayoutTree,
                            onApply: applyLayoutTree,
                          }}
                          openWith={openWithRows}
                          onSetHandler={setHandlerChoice}
                          appSettings={appSettingSections}
                          onSetAppSetting={setSetting}
                          worldName={worldName}
                          slotSections={[
                            {
                              slot: 'sidebar',
                              title: 'Sidebar',
                              note: 'What this layout puts in the sidebar slot.',
                              builtinDescription: 'The sidebar Mod ships with.',
                              noneDescription:
                                'No sidebar. Come back here with ⌘, to undo it.',
                              fills: choicesFor('sidebar'),
                              fill: sidebarFill,
                              onSet: (fill) => setSlot('sidebar', fill),
                            },
                            {
                              slot: 'header',
                              title: 'Header',
                              note: 'The strip along the top of the window.',
                              builtinDescription:
                                'Space tabs, when you have them switched on.',
                              noneDescription:
                                'Nothing along the top. ⌘, to undo it.',
                              fills: choicesFor('header'),
                              fill: headerFill,
                              onSet: (fill) => setSlot('header', fill),
                              // ONLY THE HEADER OFFERS THIS TODAY. The sidebar's width
                              // and collapse are device-local and already reachable
                              // (drag, ⌘B); giving it a second, synced mechanism would
                              // be two switches for one surface disagreeing with each
                              // other. The doc shape carries geometry for every slot,
                              // so the sidebar joins when that is reconciled.
                              geometry: {
                                size: headerGeometry.size,
                                reveal: headerGeometry.reveal,
                                sizeLabel: 'Height',
                                defaultSize: HEADER_HEIGHT_PX,
                                onSet: (next) => setGeometry('header', next),
                              },
                            },
                            {
                              slot: 'composer',
                              title: 'Composer',
                              note: 'A box along the bottom, for typing without opening a thread first.',
                              builtinDescription:
                                'Appears in a space with no thread open in it.',
                              noneDescription:
                                'No composer strip. ⌘T still opens a thread.',
                              fills: choicesFor('composer'),
                              fill: composerFill,
                              onSet: (fill) => setSlot('composer', fill),
                              geometry: {
                                size: composerGeometry.size,
                                reveal: composerGeometry.reveal,
                                sizeLabel: 'Height',
                                defaultSize: COMPOSER_HEIGHT_PX,
                                onSet: (next) => setGeometry('composer', next),
                              },
                            },
                          ]}
                          onClose={layout.toggleSettings}
                        />
                      </SettingsModal>
                    )}

                    {!chrome.sidebar && revealed && (
                      // biome-ignore lint/a11y/noStaticElementInteractions: hover affordance; the controls inside are the interactive elements
                      <div
                        onMouseEnter={cancelHide}
                        onMouseLeave={scheduleHide}
                        style={{ width: chrome.sidebarWidth }}
                        className="absolute inset-y-1.5 left-1.5 z-30"
                      >
                        {sidebar(true)}
                      </div>
                    )}
                  </div>

                  {packagesOpen && (
                    <PackagesModal
                      apps={installedAppList}
                      roleOf={roleOf}
                      statusOf={statusOf}
                      onOpen={openDocument}
                      onRemove={(app) => removeInstalled(app.id)}
                      onChangeIcon={iconsApp ? changeIcon : undefined}
                      onOpenSkill={(app) => {
                        setPackagesOpen(false)
                        appSkillAction(app.id)?.()
                      }}
                      onPublish={async (app, page) => {
                        const catalogId =
                          app.scope === 'world' ? readWorldPointer() : projectId
                        if (!catalogId)
                          throw new Error('The World is not ready; try again')
                        const screenshots = await Promise.all(
                          page.screenshots.map(uploadScreenshot),
                        )
                        return publishInstalled(
                          app,
                          catalogId,
                          {
                            category: page.category,
                            about: page.about,
                            screenshots,
                          },
                          page.includeSource,
                        )
                      }}
                      panes={leaves(rendered).flatMap((pane) => {
                        if (pane.content.kind !== 'document') return []
                        const docId = pane.content.docId
                        return [
                          {
                            id: pane.id,
                            title:
                              shellDocuments.find((doc) => doc.id === docId)
                                ?.title ?? 'Open pane',
                          },
                        ]
                      })}
                      onCapturePane={async (paneId) => {
                        const pane = leaves(rendered).find(
                          (leaf) => leaf.id === paneId,
                        )
                        if (!pane || pane.content.kind !== 'document')
                          throw new Error('That pane is no longer open')
                        const files = await capturePaneImages([{ id: pane.id }])
                        if (!files[0])
                          throw new Error('Could not capture that pane')
                        return new Promise<string>((resolve, reject) => {
                          const reader = new FileReader()
                          reader.onload = () => resolve(String(reader.result))
                          reader.onerror = () => reject(reader.error)
                          reader.readAsDataURL(files[0])
                        })
                      }}
                      onDiscover={openDiscover}
                      sourceOrigin={(app) =>
                        sourceOrigin(repo as never, app.id)
                      }
                      updates={packageUpdates}
                      onUpdate={async (update) => {
                        const worldId = readWorldPointer()
                        await updateFromRelease(
                          repo as never,
                          {
                            projectId: activeProjectId,
                            worldId: worldId ?? undefined,
                          },
                          update.app.id,
                          update.release,
                          update.releaseId,
                        )
                        setUpdatesChecked((n) => n + 1)
                      }}
                      projectId={projectId}
                      onClose={() => setPackagesOpen(false)}
                    />
                  )}
                </div>
              </SpaceSessionProvider>
            </ComposerSlotProvider>
          </WindowProvider>
        </SelectionProvider>
      </ComposerRefs.Provider>
    </AppIconsProvider>
  )
}
